/**
 * JC Jewelry Picker — Memphis Diamond Company (v2.14.5 — "(Default)" ring-size label matches price-caption italic)
 *
 * CHANGED in v2.12.0:
 *   - THE DETAILS block now renders in the LEFT column, UNDER the product media
 *     (matching the Paloma reference), instead of inside the right-hand picker.
 *     It is injected into the media column and re-rendered there on async shape
 *     enrichment. Layout is the design's two-column form (Style Number +
 *     Compatible Diamond Shapes with shape icons | Color, Clarity, Compatible
 *     Carat Range, Metal).
  *   - v2.14.0 (client markup round):
 *       * product title KEPT on the card, with a leading "The " stripped
 *       * shape labels read "Oval lab diamond"
 *       * carat options display as 1ct / 1.5ct / 2ct / 3ct (label-only mapping)
 *       * ring size gains a "Size later" option
 *       * "Choose your lab diamond" picker hidden (SHOW_DIAMOND_PICKER)
 *       * Add to cart button background forced to #474747
 *   - v2.14.2: "Diamond Engagement Rings" breadcrumb now links to the homepage
 *     Shop All grid (`/#shop-all`) instead of `/collections/engagement-rings`.
 *   - v2.14.3: Ring Size now defaults to 6.5 whenever that size exists in the
 *     product's ring-size list. The default option's label reads "6.5 (Default)"
 *     — state.size initializes to the default value and the hidden
 *     properties[Ring Size] cart-form input is seeded on load — previously that
 *     property was only ever written after a manual dropdown change event, so a
 *     customer who never touched the dropdown added a ring with no size at all.
 *   - v2.14.5: "(Default)" in the Ring Size closed select now uses the same
 *     Red Hat Text italic treatment as the price caption ("Complete ring with
 *     lab diamond included"). Native <option> text can't style a substring, so
 *     a non-interactive face overlay renders "6.5" + italic "(Default)" when
 *     the default size is selected; select values, change handlers, and cart
 *     properties are unchanged. Also loads Red Hat Text italic so the style
 *     isn't browser-synthesized.
 *   - v2.14.4: earlier attempt styled the whole default <option> (broken CSS
 *     variable; option styles don't apply to the closed select face).
 *   - v2.13.9: breadcrumb order is now Home / Collection / Product.
 *   - v2.13.8: the price now matches the Figma spec exactly — Red Hat Display,
 *     500 Medium, 40px, line-height 105%, -3% letter-spacing (was Fraunces 600,
 *     which read too heavy/large). Buttons/accents stay on the Fraunces stack.
 *   - v2.13.7: load the Figma fonts via Google Fonts (Red Hat Display 400/500/
 *     600/700, Red Hat Text 400/500/600, Fraunces 400) instead of relying on the
 *     theme font picker, and apply explicit stacks — headings: Red Hat Display,
 *     body: Red Hat Text, accent/buttons/prices: Fraunces. The product title and
 *     THE DETAILS heading no longer copy the theme heading font (Fraunces); they
 *     use Red Hat Display. UI/CSS only — no logic/data changes.
 *   - v2.13.6: added the italic "Complete ring with lab diamond included"
 *     caption beside the price (overridable via meta.priceCaption); on mobile,
 *     THE DETAILS moves BELOW the card (title + price first) via a 749px
 *     matchMedia listener, while desktop keeps it under the media.
 *   - v2.13.5: the diamond list is now a SINGLE CSS grid (repeat(5,1fr) on
 *     desktop, repeat(3,1fr) on mobile) instead of two flex containers. This
 *     stops the sub-pixel wrap that pushed the 5th card to a 2nd row while
 *     collapsed, and removes the "row 2 has one card, rest start on row 3"
 *     artifact after See more. Collapse now hides cards past the first row via
 *     :nth-child, so exactly one full row shows until See more is clicked and
 *     every revealed row is fully packed.
 *   - v2.13.4: on mobile, "CHOOSE YOUR LAB DIAMOND" now matches the input
 *     label metrics (16px, same line-height/letter-spacing) and the "See more"
 *     button stays on one line.
 *   - v2.13.3: hide the theme divider(s) above the card and raise the picker
 *     card to the top of the product-info column so its title + #FAF8F9
 *     background align with the top of the product media.
 *   - v2.13.2: the in-card product title is now ALWAYS rendered by the script
 *     (native theme title hidden) so it reliably appears above the price; added
 *     vertical spacing between the first diamond row and the expanded rows so
 *     selection rings no longer overlap the row above.
 *   - v2.13.1: the product title is relocated INTO the selection card, above
 *     the price (design layout), keeping the theme's own title font/styling
 *     (the node is moved, with an inline font-family safety net); the native
 *     title's original slot is vacated so it is not duplicated.
 *   - v2.13.0: added the design breadcrumb (Collection / Home / Product) above
 *     the media; THE DETAILS heading now inherits the theme's heading font
 *     (copied from the product title) so it matches other headings; Style
 *     Number now ALWAYS renders the live product/variant SKU (ShopifyAnalytics
 *     -> variant -> DOM), falling back to the metafield only if no SKU exists.
 *   - v2.12.1: fixed Preview eye icon clipping (added SVG viewBox) and added a
 *     page-margin gutter to THE DETAILS on mobile so it aligns with the title.
 *   - Palette retuned to the design tokens: selection panel background #FAF8F9
 *     (32px pad), primary ink #16110D, lines/secondary #16202C, square 56px
 *     selects with rgba(22,32,44,.8) borders. Navy #061B42 accents replaced
 *     throughout (selectors, price, diamond grid, preview modal, details).
 *
 * v2.15.0 — native Master+variants catalog:
 *   Paloma dropdown UI/CSS kept. Sibling page hops, store-wide product fetch,
 *   0.50ct/rose-gold title fallbacks, and Lab Diamond dual-cart are off.
 *   Shape/Metal/Carat drive Shopify variants on the same product.
 * v2.15.2 — dropdowns now drive Horizon media:
 *   Filename regex was the wrong lever (CDN names often have no metal
 *   code). Gallery now follows variant.featured_media id/src, and the
 *   hidden native variant-picker is synced so media-gallery replaces.
 */

(function () {
  'use strict';

  var METAL_DEFS = [
    { match: '14K Rose Gold',   label: '14k', color: 'rgb(230,187,172)', family: 'rose'   },
    { match: '18K Rose Gold',   label: '18k', color: 'rgb(210,162,148)', family: 'rose'   },
    { match: '14K White Gold',  label: '14k', color: 'rgb(220,220,225)', family: 'white'  },
    { match: '18K White Gold',  label: '18k', color: 'rgb(200,205,215)', family: 'white'  },
    { match: '14K Yellow Gold', label: '14k', color: 'rgb(225,195,90)',  family: 'yellow' },
    { match: '18K Yellow Gold', label: '18k', color: 'rgb(210,180,70)',  family: 'yellow' },
    { match: 'Platinum',        label: 'PT',  color: 'rgb(185,190,200)', family: 'white'  }
  ];

  var METAL_ORDER = [
    '14K Rose Gold',   '18K Rose Gold',
    '14K White Gold',  '18K White Gold',
    '14K Yellow Gold', '18K Yellow Gold',
    'Platinum'
  ];

  var CARAT_ORDER = [
    '0.50 - 0.99 ct',
    '1.00 - 1.49 ct',
    '1.50 - 1.99 ct',
    '2.00+ ct'
  ];

  var CERT_BY_SHAPE = {
    round: 'https://viw-us.s3.amazonaws.com/c/777605430.jpg',
    oval: 'https://viw-us.s3.amazonaws.com/c/781623910.jpg',
    emerald: 'https://viw-us.s3.amazonaws.com/c/748501478.jpg',
    pear: 'https://viw-us.s3.amazonaws.com/c/775641627.jpg',
    princess: 'https://viw-us.s3.amazonaws.com/c/640481407.jpg',
    radiant: 'https://viw-us.s3.amazonaws.com/c/748501478.jpg'
  };

  var SHAPE_KEYWORDS = ['radiant', 'princess', 'pear', 'emerald', 'oval', 'round'];

  var SHAPE_ORDER = ['round', 'oval', 'emerald', 'pear', 'princess', 'radiant',
                     'cushion', 'asscher', 'heart', 'marquise'];

  var FIRST_ROW_COUNT = 5;

  var NO_DIAMOND_PROP_VALUE = 'Setting only (no diamond)';

  /* v2.14.0 — client markup round. Flip these to restore prior behaviour. */
  var SHOW_PRODUCT_TITLE  = true;   // title shows on the card (leading "The " stripped)
  var SHOW_DIAMOND_PICKER = false;  // stone grid crossed out; ring ships with diamond
  var RING_SIZE_LATER     = 'Size later';
  var ADD_TO_CART_BG      = '#474747';

  /* v2.15.0 — new catalog is Master SKU + Metal/Shape/Carat variants on ONE product.
   * Keep Paloma UI/CSS. Turn OFF old exploded-catalog logic:
   *   - no full-page hop to sibling products
   *   - no /products.json store crawl
   *   - no 0.50ct / rose-gold title fallbacks
   *   - no Lab Diamond dual-cart add
   */
  var NATIVE_VARIANT_MODE = true;
  var DISABLE_SIBLING_NAV = true;
  var DISABLE_STORE_PRODUCT_FETCH = true;
  var DISABLE_DIAMOND_CART = true;

  // v2.14.3 — the ring size preselected when it exists in the product's list.
  // Change this single constant to move the default to a different size.
  var RING_SIZE_DEFAULT   = '6.5';

  // v2.13.6 — italic caption shown beside the price (design requirement).
  // Overridable per product via window._jcMeta[pid].priceCaption.
  var PRICE_CAPTION = 'Complete ring with lab diamond included';

  // v2.13.6 — mobile breakpoint (matches the CSS 749px) for DETAILS placement.
  var _mdcMobileMQ = window.matchMedia('(max-width: 749px)');

  // Shared chevron used by all .mdc-select dropdowns.
  var SELECT_CHEVRON = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' fill='none'%3E%3Cpath fill='%2316202C' d='M.4.4a.7.7 0 0 1 1 0L5.5 4.5 9.6.4a.7.7 0 1 1 1 1L6 6a.7.7 0 0 1-1 0L.4 1.4a.7.7 0 0 1 0-1Z'/%3E%3C/svg%3E\")";

  window._mdcInjected = window._mdcInjected || {};

  if (!window._mdcFetchPatched) {
    window._mdcFetchPatched  = true;
    window._mdcCartCallbacks = [];

    var _origFetch = window.fetch.bind(window);
    window._mdcOrigFetch = _origFetch;
    window.fetch = function(input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isRingAdd = url.indexOf('/cart/add') !== -1 &&
                      !(init && init.body && String(init.body).indexOf('"_mdc_diamond"') !== -1);

      var promise = _origFetch(input, init);

      if (isRingAdd && window._mdcCartCallbacks.length) {
        promise.then(function(res) {
          var callbacks = window._mdcCartCallbacks.slice();
          window._mdcCartCallbacks = window._mdcCartCallbacks.filter(function(c) { return !c.once; });
          callbacks.forEach(function(c) {
            try { c.fn(); } catch(e) { console.error('[MDC] cart callback error', e); }
          });
          return res;
        });
      }

      return promise;
    };
  }

  function fmtDollars(d) {
    return '$' + Number(d).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  function centsToDisplay(cents) { return fmtDollars(Math.round(cents / 100)); }
  function escAttr(s) { return (s == null ? '' : String(s)).replace(/"/g, '&quot;'); }
  function escHtml(s) {
    return (s == null ? '' : String(s))
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function getMetalDef(metalStr) {
    if (!metalStr) return null;
    for (var i = 0; i < METAL_DEFS.length; i++) {
      if (metalStr === METAL_DEFS[i].match) return METAL_DEFS[i];
    }
    var mu = metalStr.toUpperCase();
    for (var j = 0; j < METAL_DEFS.length; j++) {
      if (mu.indexOf(METAL_DEFS[j].match.toUpperCase()) !== -1) return METAL_DEFS[j];
    }
    return null;
  }
  function getMetalColor(metalStr) { var d = getMetalDef(metalStr); return d ? d.color : 'rgb(200,200,200)'; }
  function getMetalLabel(metalStr) {
    var def = getMetalDef(metalStr);
    if (def) return def.label;
    var mu = (metalStr || '').toUpperCase();
    if (mu.indexOf('PT') !== -1 || mu.indexOf('PLAT') !== -1) return 'PT';
    if (mu.indexOf('18K') !== -1) return '18k';
    if (mu.indexOf('14K') !== -1) return '14k';
    return (metalStr || '?').substring(0, 3);
  }

  function parseTitle(title) {
    if (!title || typeof title !== 'string') return null;
    var slashIdx = title.indexOf(' / ');
    if (slashIdx === -1) return null;
    var before = title.substring(0, slashIdx);
    var carat  = title.substring(slashIdx + 3).trim();
    if (!carat) return null;
    var dashIdx = before.lastIndexOf(' - ');
    if (dashIdx === -1) return null;
    var style = before.substring(0, dashIdx).trim();
    var metal = before.substring(dashIdx + 3).trim();
    if (!style || !metal) return null;
    return { style: style, metal: metal, carat: carat };
  }

  function normalizeStyleKey(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function inferStyleFromTitle(title) {
    var raw = String(title || '').trim();
    if (!raw) return '';
    var slashIdx = raw.indexOf(' / ');
    var before = slashIdx === -1 ? raw : raw.substring(0, slashIdx).trim();
    var dashIdx = before.lastIndexOf(' - ');
    return dashIdx === -1 ? before : before.substring(0, dashIdx).trim();
  }

  function inferMetalFromText(title) {
    var raw = String(title || '');
    if (!raw) return '';

    for (var i = 0; i < METAL_ORDER.length; i++) {
      if (raw.indexOf(METAL_ORDER[i]) !== -1) return METAL_ORDER[i];
    }

    var u = raw.toUpperCase();
    if (u.indexOf('PLAT') !== -1 || u.indexOf(' PT') !== -1) return 'Platinum';
    if (u.indexOf('18K') !== -1 && u.indexOf('ROSE') !== -1) return '18K Rose Gold';
    if (u.indexOf('14K') !== -1 && u.indexOf('ROSE') !== -1) return '14K Rose Gold';
    if (u.indexOf('18K') !== -1 && u.indexOf('WHITE') !== -1) return '18K White Gold';
    if (u.indexOf('14K') !== -1 && u.indexOf('WHITE') !== -1) return '14K White Gold';
    if (u.indexOf('18K') !== -1 && u.indexOf('YELLOW') !== -1) return '18K Yellow Gold';
    if (u.indexOf('14K') !== -1 && u.indexOf('YELLOW') !== -1) return '14K Yellow Gold';

    return '';
  }

  function inferCaratFromText(title) {
    var u = String(title || '').toUpperCase().replace(/\s+/g, ' ');
    if (u.indexOf('2.00') !== -1 || u.indexOf('2.0+') !== -1 || /2\s*\+\s*CT/.test(u)) return '2.00+ ct';
    if (u.indexOf('1.50') !== -1) return '1.50 - 1.99 ct';
    if (u.indexOf('1.00') !== -1) return '1.00 - 1.49 ct';
    if (u.indexOf('0.50') !== -1) return '0.50 - 0.99 ct';
    return '';
  }

  function inferCurrentParsed(meta) {
    var strict = parseTitle(meta && meta.currentTitle);
    if (strict) return strict;

    var title = meta && meta.currentTitle ? String(meta.currentTitle) : '';
    var style = inferStyleFromTitle(title);
    var metal = inferMetalFromText(title);
    var carat = inferCaratFromText(title);

    var titles = Object.keys((meta && meta.siblings) || {});
    var styleKey = normalizeStyleKey(style);

    for (var i = 0; i < titles.length; i++) {
      var p = parseTitle(titles[i]);
      if (!p) continue;
      if (styleKey && normalizeStyleKey(p.style) !== styleKey) continue;

      if (!style) style = p.style;
      if (!metal) metal = p.metal;
      if (!carat) carat = p.carat;
      if (style && metal && carat) break;
    }

    if (!style) style = title || 'Ring';
    if (!NATIVE_VARIANT_MODE) {
      if (!metal) metal = '14K Rose Gold';
      if (!carat) carat = '0.50 - 0.99 ct';
    }

    return { style: style, metal: metal, carat: carat };
  }

  function normalizeShapeKey(raw) {
    var s = String(raw || '').toLowerCase().trim();
    if (!s) return '';
    s = s
      .replace(/diamond/g, '')
      .replace(/cut/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!s) return '';
    if (s === 'rb' || s === 'rd' || s.indexOf('round') !== -1) return 'round';
    if (s === 'ov' || s.indexOf('oval') !== -1) return 'oval';
    if (s === 'em' || s.indexOf('emerald') !== -1) return 'emerald';
    if (s === 'pe' || s.indexOf('pear') !== -1) return 'pear';
    if (s === 'pr' || s.indexOf('princess') !== -1) return 'princess';
    if (s === 'ra' || s.indexOf('radiant') !== -1) return 'radiant';
    if (s.indexOf('cushion') !== -1) return 'cushion';
    if (s.indexOf('asscher') !== -1) return 'asscher';
    if (s.indexOf('heart') !== -1) return 'heart';
    if (s.indexOf('marquise') !== -1) return 'marquise';
    return '';
  }

  function inferShapeFromTitle(title) {
    var u = String(title || '').toUpperCase();
    if (u.indexOf('ROUND') !== -1) return 'round';
    if (u.indexOf('OVAL') !== -1) return 'oval';
    if (u.indexOf('EMERALD') !== -1) return 'emerald';
    if (u.indexOf('PEAR') !== -1) return 'pear';
    if (u.indexOf('PRINCESS') !== -1) return 'princess';
    if (u.indexOf('RADIANT') !== -1) return 'radiant';
    return '';
  }

  function inferShapeFromUrl(url) {
    var u = String(url || '').toLowerCase();
    for (var i = 0; i < SHAPE_KEYWORDS.length; i++) {
      if (u.indexOf(SHAPE_KEYWORDS[i]) !== -1) return SHAPE_KEYWORDS[i];
    }
    return '';
  }

  function resolveProductShapeKey(meta) {
    var key = normalizeShapeKey(meta && meta.diamondShapeKey);
    if (key) return key;

    key = normalizeShapeKey(meta && meta.diamondShape);
    if (key) return key;

    key = inferShapeFromTitle(meta && meta.currentTitle);
    if (key) return key;

    key = inferShapeFromUrl(meta && meta.currentUrl);
    if (key) return key;

    key = inferShapeFromUrl(window.location.pathname);
    if (key) return key;

    return 'round';
  }

  function shapeDisplayName(key) {
    var k = normalizeShapeKey(key) || String(key || '').toLowerCase();
    if (!k) return 'Diamond';
    return k.charAt(0).toUpperCase() + k.slice(1) + ' lab diamond';
  }

  function getHardcodedCertUrlByShape(shapeKey) {
    return CERT_BY_SHAPE[shapeKey] || CERT_BY_SHAPE.round;
  }

  function caratShort(caratStr) {
    if (!caratStr) return '?';
    var cleaned = caratStr.replace(/ ct$/i, '').trim();
    return cleaned.indexOf(' - ') !== -1 ? cleaned.split(' - ')[0].trim() : cleaned;
  }

  function buildSiblingsIndex(currentParsed, siblings, shapeFilter) {
    var byMetal = {};
    var byCarat = {};
    var titles  = Object.keys(siblings || {});
    var currentStyleKey = normalizeStyleKey(currentParsed && currentParsed.style);

    for (var i = 0; i < titles.length; i++) {
      var t = titles[i];
      var p = parseTitle(t);
      if (!p) continue;

      var pStyleKey = normalizeStyleKey(p.style);
      if (currentStyleKey && pStyleKey && pStyleKey !== currentStyleKey) continue;

      var url = siblings[t];
      if (!url) continue;

      if (shapeFilter) {
        var siblingShape = inferShapeFromUrl(url);
        if (siblingShape && siblingShape !== shapeFilter) continue;
      }

      if (!byMetal[p.metal]) byMetal[p.metal] = {};
      byMetal[p.metal][p.carat] = url;
      if (!byCarat[p.carat]) byCarat[p.carat] = {};
      byCarat[p.carat][p.metal] = url;
    }

    var curUrl = window.location.pathname + window.location.search;
    if (!byMetal[currentParsed.metal]) byMetal[currentParsed.metal] = {};
    byMetal[currentParsed.metal][currentParsed.carat] = curUrl;
    if (!byCarat[currentParsed.carat]) byCarat[currentParsed.carat] = {};
    byCarat[currentParsed.carat][currentParsed.metal] = curUrl;

    var metals = Object.keys(byMetal).sort(function(a, b) {
      var ai = METAL_ORDER.indexOf(a), bi = METAL_ORDER.indexOf(b);
      if (ai === -1) ai = 99; if (bi === -1) bi = 99;
      return ai - bi;
    });

    var carats = Object.keys(byCarat).sort(function(a, b) {
      var ai = CARAT_ORDER.indexOf(a), bi = CARAT_ORDER.indexOf(b);
      if (ai === -1) ai = 99; if (bi === -1) bi = 99;
      return ai - bi;
    });

    return { byMetal: byMetal, byCarat: byCarat, metals: metals, carats: carats };
  }

  function urlForMetal(targetMetal, currentCarat, idx) {
    var caratMap = idx.byMetal[targetMetal];
    if (!caratMap) return null;
    if (caratMap[currentCarat]) return caratMap[currentCarat];
    for (var i = 0; i < CARAT_ORDER.length; i++) {
      if (caratMap[CARAT_ORDER[i]]) return caratMap[CARAT_ORDER[i]];
    }
    var keys = Object.keys(caratMap);
    return keys.length ? caratMap[keys[0]] : null;
  }

  function urlForCarat(targetCarat, currentMetal, idx) {
    var metalMap = idx.byCarat[targetCarat];
    if (!metalMap) return null;

    if (metalMap[currentMetal]) return metalMap[currentMetal];

    var currentDef = getMetalDef(currentMetal);
    if (currentDef) {
      for (var i = 0; i < METAL_ORDER.length; i++) {
        var candidate = METAL_ORDER[i];
        if (candidate === currentMetal) continue;
        var candidateDef = getMetalDef(candidate);
        if (candidateDef && candidateDef.family === currentDef.family && metalMap[candidate]) {
          return metalMap[candidate];
        }
      }
    }

    return null;
  }

  /* ======================================================================
   * Async sibling resolution via Shopify Products API (from v2.9.0)
   * ====================================================================== */

  var MDC_CACHE_KEY = 'mdc_all_products_v2';
  var MDC_CACHE_TTL = 30 * 60 * 1000;

  function getShapeFromProductTags(tags) {
    if (!tags) return '';
    var t = (typeof tags === 'string' ? tags : (Array.isArray(tags) ? tags.join(',') : '')).toLowerCase();
    if (t.indexOf('diamond shape: round') !== -1) return 'round';
    if (t.indexOf('diamond shape: oval') !== -1) return 'oval';
    if (t.indexOf('diamond shape: emerald') !== -1) return 'emerald';
    if (t.indexOf('diamond shape: pear') !== -1) return 'pear';
    if (t.indexOf('diamond shape: princess') !== -1) return 'princess';
    if (t.indexOf('diamond shape: radiant') !== -1) return 'radiant';
    return '';
  }

  function fetchAllStoreProducts(callback) {
    try {
      var raw = sessionStorage.getItem(MDC_CACHE_KEY);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached.ts && (Date.now() - cached.ts) < MDC_CACHE_TTL) {
          console.log('[MDC] Using cached product data (' + cached.products.length + ' products)');
          callback(cached.products);
          return;
        }
      }
    } catch (e) { /* cache miss or parse error */ }

    var allProducts = [];
    var page = 1;
    var apiFetch = window._mdcOrigFetch || window.fetch;

    function fetchPage() {
      apiFetch('/products.json?limit=250&page=' + page + '&fields=title,handle,tags')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var products = data.products || [];
          allProducts = allProducts.concat(products);

          if (products.length === 250 && page < 10) {
            page++;
            fetchPage();
          } else {
            console.log('[MDC] Fetched ' + allProducts.length + ' products from API (' + page + ' pages)');
            try {
              sessionStorage.setItem(MDC_CACHE_KEY, JSON.stringify({
                ts: Date.now(),
                products: allProducts
              }));
            } catch (e) { /* sessionStorage full or disabled */ }
            callback(allProducts);
          }
        })
        .catch(function(err) {
          console.warn('[MDC] Products API fetch error:', err.message);
          callback(allProducts.length ? allProducts : []);
        });
    }

    fetchPage();
  }

  function findSiblingsFromProducts(products, styleKey, shapeFilter) {
    var siblings = {};

    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var parsed = parseTitle(p.title);
      if (!parsed) continue;
      if (normalizeStyleKey(parsed.style) !== styleKey) continue;

      if (shapeFilter) {
        var prodShape = getShapeFromProductTags(p.tags) || inferShapeFromUrl('/products/' + p.handle);
        if (prodShape && prodShape !== shapeFilter) continue;
      }

      siblings[p.title] = '/products/' + p.handle;
    }

    return siblings;
  }

  // v2.11.0 — one representative product URL per detected shape for a style.
  function findShapeSiblingsFromProducts(products, styleKey) {
    var out = {};
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var parsed = parseTitle(p.title);
      if (!parsed) continue;
      if (normalizeStyleKey(parsed.style) !== styleKey) continue;

      var shape = getShapeFromProductTags(p.tags) || inferShapeFromUrl('/products/' + p.handle);
      if (!shape) continue;
      var name = shape.charAt(0).toUpperCase() + shape.slice(1);
      if (!out[name]) out[name] = '/products/' + p.handle;
    }
    return out;
  }

  /* ====================================================================== */

  // v2.13.7 — load the Figma fonts via Google Fonts so we never depend on the
  // theme font picker (and never fall back to the theme heading font, Fraunces,
  // for Red Hat Display).
  function injectFonts() {
    if (document.getElementById('mdc-figma-fonts')) return;
    var head = document.head || document.getElementsByTagName('head')[0];
    if (!head) return;

    var pre1 = document.createElement('link');
    pre1.rel = 'preconnect';
    pre1.href = 'https://fonts.googleapis.com';

    var pre2 = document.createElement('link');
    pre2.rel = 'preconnect';
    pre2.href = 'https://fonts.gstatic.com';
    pre2.crossOrigin = 'anonymous';

    var link = document.createElement('link');
    link.id = 'mdc-figma-fonts';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2' +
      '?family=Red+Hat+Display:wght@400;500;600;700' +
      '&family=Red+Hat+Text:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600' +
      '&family=Fraunces:opsz,wght@9..144,400' +
      '&display=swap';

    head.appendChild(pre1);
    head.appendChild(pre2);
    head.appendChild(link);
  }

  function injectCSS() {
    if (document.getElementById('mdc-picker-styles')) return;
    var s = document.createElement('style');
    s.id = 'mdc-picker-styles';
    s.textContent = [
      /* v2.13.7 — Figma font stacks (display / body / accent) */
      ":root{--mdc-font-display:'Red Hat Display',Helvetica,Arial,sans-serif;--mdc-font-body:'Red Hat Text','Red Hat Display',Helvetica,Arial,sans-serif;--mdc-font-accent:'Fraunces',Georgia,serif}",

      'variant-picker fieldset,variant-picker .variant-option{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;white-space:nowrap!important;border:0!important}',
      '.mdc-media-hide{display:none!important;height:0!important;margin:0!important;padding:0!important;overflow:hidden!important;border:0!important}',
      /* Desktop + mobile: pull other-look slides OUT of the scroller.
         Junior left them in on desktop (display:revert) so timelines lived —
         that broke thumbnail clicks and flashed every look on scroll.
         Horizon select() already filters [hidden]; we remap thumbs to visible index. */
      'media-gallery slideshow-slide.mdc-media-hide,media-gallery slideshow-slide[hidden]:not([reveal]){display:none!important;flex:0 0 0!important;width:0!important;min-width:0!important;max-width:0!important;height:0!important;margin:0!important;padding:0!important;overflow:hidden!important;border:0!important;pointer-events:none!important;visibility:hidden!important;scroll-snap-align:none!important;content-visibility:hidden!important}',
      'media-gallery .slideshow-controls__dots li.mdc-media-hide,media-gallery .slideshow-controls__dots li[hidden],media-gallery .media-gallery__mobile-controls li.mdc-media-hide,media-gallery .media-gallery__mobile-controls li[hidden],media-gallery .media-gallery__mobile-controls .mdc-media-hide{display:none!important;width:0!important;min-width:0!important;height:0!important;margin:0!important;padding:0!important;overflow:hidden!important;flex:none!important;pointer-events:none!important}',
      'slideshow-controls li.mdc-media-hide,.slideshow-controls__thumbnail.mdc-media-hide,.slideshow-controls__thumbnail[hidden],.dialog-thumbnails-list__thumbnail.mdc-media-hide,.dialog-thumbnails-list__thumbnail[hidden]{display:none!important;height:0!important;width:0!important;min-height:0!important;min-width:0!important;margin:0!important;padding:0!important;overflow:hidden!important;border:0!important;flex:0 0 0!important}',
      'media-gallery.mdc-gallery-ready slideshow-slides{scroll-behavior:auto}',

      /* v2.12 — selection panel: design #FAF8F9 background, 32px padding */
      '.mdc-picker{font-family:var(--mdc-font-body);color:#16110D;box-sizing:border-box;background:#FAF8F9;padding:32px}',
      '.mdc-picker *{box-sizing:border-box}',
      '.mdc-section{margin-bottom:20px}',

      /* stacked field + full-width square dropdown (design tokens) */
      '.mdc-field{margin-bottom:20px}',
      '.mdc-field-label{font-family:var(--mdc-font-display);display:block;font-size:16px;font-weight:500;line-height:1.05;letter-spacing:-0.06em;color:#16110D;margin-bottom:8px}',
      '.mdc-select{width:100%;min-height:56px;padding:16px 44px 16px 16px;border:1px solid rgba(22,32,44,.8);border-radius:0;font-family:inherit;font-size:16px;font-weight:500;letter-spacing:-0.04em;color:#16110D;background-color:#fff;appearance:none;-webkit-appearance:none;-moz-appearance:none;cursor:pointer;outline:none;line-height:1.5;background-image:' + SELECT_CHEVRON + ';background-repeat:no-repeat;background-position:right 16px center}',
      '.mdc-select:hover{border-color:#16202C}',
      '.mdc-select:focus{border-color:#16110D;box-shadow:0 0 0 2px rgba(22,17,13,.08)}',
      '.mdc-select option[disabled]{color:#a9adb5}',

      /* v2.14.5 — Ring Size closed face: size stays regular; "(Default)" matches
         .mdc-price-caption (Red Hat Text / italic / 500). The native <select>
         stays fully interactive underneath (text hidden only while default). */
      '.mdc-ring-size-wrap{position:relative}',
      '.mdc-ring-size-face{position:absolute;inset:0;z-index:1;display:none;align-items:center;padding:16px 44px 16px 16px;pointer-events:none;font-family:var(--mdc-font-body);font-size:16px;font-weight:500;letter-spacing:-0.04em;line-height:1.5;color:#16110D;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
      '.mdc-ring-size-wrap.is-default-selected .mdc-ring-size-face{display:flex}',
      '.mdc-ring-size-wrap.is-default-selected .mdc-select[data-mdc-ring-size]{color:transparent;-webkit-text-fill-color:transparent}',
      '.mdc-ring-size-face__default{font-family:var(--mdc-font-body);font-style:italic;font-weight:500;color:rgba(22,17,13,.72)}',

      /* v2.13.1 — product title slot at the top of the card */
      '.mdc-title-slot{margin:0 0 12px;padding:0}',
      '.mdc-title-slot > *{margin-top:0!important;margin-bottom:0!important;max-width:100%}',
      '.mdc-synth-title{font-family:var(--mdc-font-display);margin:0;font-size:40px;font-weight:500;line-height:1.05;letter-spacing:-0.03em;color:#16110D}',
      '.mdc-synth-brief{font-family:var(--mdc-font-body,var(--mdc-font-display));margin:8px 0 0!important;font-size:16px;font-weight:400;line-height:1.35;letter-spacing:-0.01em;color:#6b6560}',

      '.mdc-price-display{margin-bottom:24px;color:#16110D}',
      '.mdc-price-line{display:flex;align-items:baseline;gap:12px;flex-wrap:wrap}',
      '.mdc-price-total{font-family:var(--mdc-font-display);font-weight:500;font-size:40px;line-height:1.05;letter-spacing:-0.03em;color:#16110D}',
      '.mdc-price-caption{font-family:var(--mdc-font-body);font-style:italic;font-weight:500;font-size:16px;line-height:1.3;color:rgba(22,17,13,.72)}',
      '.mdc-price-breakdown{margin-top:8px;font-size:16px;line-height:1.6}',
      '.mdc-price-ring{font-weight:400}',
      '.mdc-price-diamond{color:rgba(22,17,13,.65)}',

      '.mdc-diamond-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}',
      '.mdc-diamond-title{font-family:var(--mdc-font-display);font-size:16px;font-weight:500;letter-spacing:0.01em;text-transform:uppercase;color:#16110D}',
      '.mdc-diamond-see-more{font-size:13px;font-weight:500;color:#16110D;border:1px solid rgba(22,32,44,.35);border-radius:0;padding:6px 14px;background:#fff;cursor:pointer;font-family:var(--mdc-font-accent);white-space:nowrap;flex:none;transition:border-color .2s}',
      '.mdc-diamond-see-more:hover{border-color:#16202C}',
      '.mdc-diamond-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:16px}',
      '.mdc-diamond-section:not(.is-expanded) .mdc-diamond-card:nth-child(n+6){display:none}',
      '.mdc-diamond-card{min-width:0;text-align:center;cursor:pointer;border:none;background:none;font-family:inherit;padding:0;transition:opacity .2s}',
      '.mdc-diamond-card:hover{opacity:.8}',
      '.mdc-diamond-card.is-selected .mdc-diamond-img-wrap{box-shadow:0 0 0 2px #16110D;border-radius:50%}',
      '.mdc-diamond-img-wrap{width:74px;height:74px;margin:2px auto 8px;border-radius:50%;overflow:hidden;border:1px solid rgba(22,17,13,.12);display:flex;align-items:center;justify-content:center;background:#fff}',
      '.mdc-diamond-img-wrap img{width:74px;height:74px;object-fit:cover;border-radius:50%}',
      '.mdc-diamond-img-wrap .mdc-diamond-placeholder{width:40px;height:40px;opacity:.3}',
      '.mdc-diamond-card--none .mdc-diamond-img-wrap{border-style:dashed;border-color:rgba(22,32,44,.35)}',
      '.mdc-diamond-card--none .mdc-diamond-none-icon{width:34px;height:34px;color:#16202C;opacity:.5}',
      '.mdc-diamond-card--none .mdc-diamond-price{color:rgba(22,17,13,.5);font-weight:400}',
      '.mdc-diamond-info{font-size:12px;color:#16110D;line-height:1.4}',
      '.mdc-diamond-info .mdc-diamond-price{display:block;font-weight:500}',
      '.mdc-diamond-preview{display:inline-flex;align-items:center;gap:4px;line-height:1;font-size:11px;color:#16110D;margin-top:6px;padding:1px 0 0;background:none;border:none;cursor:pointer;font-family:inherit;overflow:visible;text-decoration:underline;text-underline-offset:2px}',
      '.mdc-diamond-preview svg{width:15px;height:11px;flex:none;display:block}',
      '.mdc-diamond-empty{font-size:13px;color:rgba(22,17,13,.5);padding:16px;text-align:center;background:rgba(22,17,13,.03);border-radius:0;width:100%}',
      '.mdc-cart-error{font-size:13px;color:#c0392b;margin-top:8px;display:none}',
      '.mdc-cart-error.is-visible{display:block}',

      /* v2.14.0 — Add to cart button uses the requested dark grey, not black */
      '.product-form__buttons button[name="add"],.product-form__submit,button[name="add"],[ref="productFormButtons"] button[type="submit"],.product-form__buttons .button--primary{background-color:' + ADD_TO_CART_BG + '!important;border-color:' + ADD_TO_CART_BG + '!important}',
      '.product-form__buttons button[name="add"]:hover,.product-form__submit:hover,button[name="add"]:hover,[ref="productFormButtons"] button[type="submit"]:hover{background-color:#3a3a3a!important;border-color:#3a3a3a!important}',

      /* v2.13 — breadcrumb (Collection / Home / Product) above the media */
      '.mdc-breadcrumb{display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin:0 0 24px;font-family:var(--mdc-font-body);font-size:14px;font-weight:500;line-height:1;color:#16202C}',
      '.mdc-breadcrumb a{color:#16202C;text-decoration:none}',
      '.mdc-breadcrumb a:hover{text-decoration:underline}',
      '.mdc-breadcrumb .mdc-breadcrumb-collection,.mdc-breadcrumb .mdc-breadcrumb-home{text-transform:uppercase}',
      '.mdc-breadcrumb-home{color:#5b6b7f}',
      '.mdc-breadcrumb-sep{opacity:.45}',
      '.mdc-breadcrumb-current{opacity:.9}',

      /* v2.12 — THE DETAILS: left column, under media, two-column form */
      '.mdc-details{display:flex;flex-wrap:wrap;gap:40px 100px;margin:40px 0 0;padding:0;color:#16110D;font-family:var(--mdc-font-body);box-sizing:border-box}',
      '.mdc-details *{box-sizing:border-box}',
      '.mdc-details-col{display:flex;flex-direction:column;gap:24px}',
      '.mdc-details-col--left{flex:1 1 280px;min-width:260px}',
      '.mdc-details-col--right{flex:0 1 220px;min-width:200px}',
      '.mdc-details-title{font-family:var(--mdc-font-display);font-size:24px;font-weight:500;line-height:1.05;letter-spacing:-0.04em;text-transform:uppercase;color:#16110D;margin:0}',
      '.mdc-detail-block{display:flex;flex-direction:column;gap:8px}',
      '.mdc-detail-label{font-family:var(--mdc-font-display);font-size:16px;font-weight:500;line-height:1.05;letter-spacing:0.01em;text-transform:uppercase;color:#16110D}',
      '.mdc-detail-value{font-size:16px;font-weight:500;line-height:1.5;letter-spacing:-0.04em;color:#16110D;opacity:.7}',
      '.mdc-shape-list{list-style:none;margin:9px 0 0;padding:0;display:flex;flex-direction:column;gap:12px;opacity:.7}',
      '.mdc-shape-item{display:flex;align-items:center;gap:16px;font-size:16px;font-weight:500;letter-spacing:-0.02em;color:#16110D}',
      '.mdc-shape-item svg{width:21px;height:21px;flex:none;color:#474747}',

      '.mdc-preview-overlay{position:fixed;inset:0;background:rgba(22,32,44,.74);z-index:9998;display:none;align-items:center;justify-content:center;padding:14px}',
      '.mdc-preview-overlay.is-open{display:flex}',
      '.mdc-preview-modal{font-family:var(--mdc-font-body);position:relative;width:min(90vw,640px);max-height:calc(100vh - 56px);overflow:auto;background:#fff;color:#16110D;padding:22px 24px 20px;border-radius:0;box-shadow:0 12px 32px rgba(0,0,0,.25)}',
      '.mdc-preview-close{position:absolute;top:10px;right:10px;border:none;background:none;cursor:pointer;color:#16202C;font-size:30px;line-height:1;padding:0;width:30px;height:30px}',
      '.mdc-preview-toplabel{font-size:11px;letter-spacing:2.6px;text-transform:uppercase;color:rgba(22,17,13,.4);margin-bottom:4px}',
      '.mdc-preview-title{font-family:var(--mdc-font-accent);font-size:44px;line-height:.98;margin:0 0 8px;font-weight:500;letter-spacing:-0.03em}',
      '.mdc-preview-id{font-size:16px;color:rgba(22,17,13,.55);margin-bottom:8px}',
      '.mdc-preview-price{font-family:var(--mdc-font-accent);font-size:34px;line-height:1;font-weight:500;letter-spacing:-0.03em;margin-bottom:2px}',
      '.mdc-preview-price-label{font-size:10px;letter-spacing:1.9px;text-transform:uppercase;color:rgba(22,17,13,.4);margin-bottom:14px}',
      '.mdc-preview-actions{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:16px}',
      '.mdc-preview-btn{font-family:var(--mdc-font-accent);display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:36px;padding:0 16px;border:1px solid rgba(22,32,44,.4);border-radius:0;background:#fff;color:#16110D;text-decoration:none;font-size:11px;letter-spacing:1.8px;text-transform:uppercase;font-weight:500}',
      '.mdc-preview-btn:hover{border-color:#16202C}',
      '.mdc-preview-btn[aria-disabled="true"]{opacity:.45;pointer-events:none}',
      '.mdc-preview-specs{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px 18px}',
      '.mdc-preview-spec-value{font-size:24px;line-height:1;font-family:inherit;font-weight:500;letter-spacing:-0.02em;margin-bottom:4px;word-break:break-word}',
      '.mdc-preview-spec-label{font-size:10px;letter-spacing:1.8px;text-transform:uppercase;color:rgba(22,17,13,.4)}',
      '@media(max-width:1080px){.mdc-preview-modal{width:min(92vw,580px)}.mdc-preview-title{font-size:38px}.mdc-preview-price{font-size:30px}.mdc-preview-spec-value{font-size:22px}}',
      '@media(max-width:749px){.mdc-picker{padding:20px}.mdc-synth-title{font-size:30px}.mdc-price-total{font-size:32px}.mdc-diamond-title{font-size:16px;line-height:1.05;letter-spacing:-0.06em;min-width:0}.mdc-diamond-grid{grid-template-columns:repeat(3,1fr)}.mdc-diamond-section:not(.is-expanded) .mdc-diamond-card:nth-child(n+4){display:none}.mdc-diamond-img-wrap{width:60px;height:60px}.mdc-diamond-img-wrap img{width:60px;height:60px}.mdc-breadcrumb{padding-left:var(--page-margin,16px);padding-right:var(--page-margin,16px)}.mdc-details{gap:32px;margin-top:32px;padding-left:var(--page-margin,16px);padding-right:var(--page-margin,16px)}.mdc-details-col--left,.mdc-details-col--right{flex:1 1 100%}.mdc-preview-overlay{padding:10px}.mdc-preview-modal{width:min(94vw,520px);max-height:calc(100vh - 28px);padding:18px 16px 16px}.mdc-preview-title{font-size:30px}.mdc-preview-price{font-size:28px}.mdc-preview-specs{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px 14px}.mdc-preview-spec-value{font-size:20px}}'
    ].join('\n');
    document.head.appendChild(s);
  }

  var DIAMOND_PLACEHOLDER_SVG = '<svg class="mdc-diamond-placeholder" viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M8 15h24l-12 16Z"/><path d="M8 15l4-6h16l4 6"/><path d="M8 15h24"/><path d="M20 31l-4-16 4-6 4 6Z"/></svg>';
  var NONE_ICON_SVG = '<svg class="mdc-diamond-none-icon" viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 14h18l-9 13Z"/><path d="M11 14l3-5h12l3 5"/><line x1="8.5" y1="8.5" x2="31.5" y2="31.5"/></svg>';
  var EYE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 11" width="16" height="11" fill="none" preserveAspectRatio="xMidYMid meet"><path fill="#16202C" d="M8 8.167c.833 0 1.542-.292 2.125-.875A2.893 2.893 0 0 0 11 5.167c0-.834-.292-1.542-.875-2.125A2.893 2.893 0 0 0 8 2.167a2.89 2.89 0 0 0-2.125.875A2.893 2.893 0 0 0 5 5.167c0 .833.292 1.541.875 2.125A2.893 2.893 0 0 0 8 8.167Zm0-1.2c-.5 0-.925-.175-1.275-.525A1.736 1.736 0 0 1 6.2 5.167c0-.5.175-.925.525-1.275.35-.35.775-.525 1.275-.525s.925.175 1.275.525c.35.35.525.775.525 1.275 0 .5-.175.925-.525 1.275-.35.35-.775.525-1.275.525Zm0 3.2c-1.622 0-3.1-.453-4.433-1.359C2.233 7.903 1.267 6.688.667 5.167c.6-1.522 1.566-2.736 2.9-3.642A7.718 7.718 0 0 1 8 .167c1.622 0 3.1.452 4.433 1.358 1.334.906 2.3 2.12 2.9 3.642-.6 1.522-1.566 2.736-2.9 3.641A7.718 7.718 0 0 1 8 10.167Z"/></svg>';

  function flattenDiamonds(diamonds) {
    if (!diamonds || !diamonds.length) return [];
    var stones = [];
    for (var i = 0; i < diamonds.length; i++) {
      var d = diamonds[i];
      if (d.stones && Array.isArray(d.stones)) {
        for (var j = 0; j < d.stones.length; j++) stones.push(d.stones[j]);
       } else if (d.carat !== undefined || d.weight !== undefined) {
        stones.push(d);
      }
    }
    return stones;
  }

  function pickFirst(stone, keys) {
    if (!stone) return '';
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (stone[k] != null && stone[k] !== '') return stone[k];
    }
    return '';
  }

  function normalizeUrl(url) {
    if (!url) return '';
    var u = String(url).trim();
    if (!u) return '';
    if (u.indexOf('//') === 0) u = window.location.protocol + u;
    return u;
  }

  function decodeImageSource(url) {
    var normalized = normalizeUrl(url);
    if (!normalized) return '';
    try {
      var parsed = new URL(normalized, window.location.origin);
      var nested = parsed.searchParams.get('url');
      if (nested) return decodeURIComponent(nested);
    } catch (e) {}
    return normalized;
  }

  function extractAssetIdFromStone(stone) {
    var explicit = pickFirst(stone, ['assetId', 'asset_id', 'diamondAssetId', 'viewerId', 'viewer_id', 'videoId', 'video_id']);
    if (explicit) return String(explicit).trim();

    var imgRaw = pickFirst(stone, ['image', 'img', 'thumbnail', 'stillImage', 'still_image']);
    if (!imgRaw && Array.isArray(stone.images) && stone.images[0]) imgRaw = stone.images[0];
    var img = decodeImageSource(imgRaw);
    if (!img) return '';
    var m = img.match(/viw-us\.s3\.amazonaws\.com\/([^\/]+)\//i);
    return m && m[1] ? m[1] : '';
  }

  function buildViewerUrl(stone) {
    var explicit = normalizeUrl(pickFirst(stone, ['video', 'videoUrl', 'video_url', 'video360Url', 'viewer', 'viewerUrl', 'viewer_url']));
    if (explicit) return explicit;
    var assetId = extractAssetIdFromStone(stone);
    return assetId ? ('https://viw-us.s3.amazonaws.com/viewer/index.html?d=' + encodeURIComponent(assetId)) : '';
  }

  function buildCertificateUrl(stone, productCertUrl) {
    var stoneCert = stone && pickFirst(stone, ['certificate', 'certUrl', 'cert_url', 'certificateUrl']);
    if (stoneCert) return normalizeUrl(stoneCert);
    return productCertUrl || CERT_BY_SHAPE.round;
  }

  function toShapeName(stone, productShapeKey) {
    var shape = pickFirst(stone, ['shape', 'diamondShape', 'diamond_shape', 'cutShape', 'cut_shape']);
    if (!shape && stone && stone.cut && String(stone.cut).trim()) shape = stone.cut;
    shape = String(shape || '').trim();

    if (!shape && productShapeKey) {
      shape = productShapeKey.charAt(0).toUpperCase() + productShapeKey.slice(1);
    }

    if (!shape) return 'Diamond';
    return shape.charAt(0).toUpperCase() + shape.slice(1).toLowerCase() + ' Diamond';
  }

  function getPreviewSpecRows(stone) {
    var cutGrade = pickFirst(stone, ['cutGrade', 'cut_grade', 'cut', 'make']) || 'N/A';
    var symmetry = pickFirst(stone, ['symmetry']) || 'N/A';
    var polish = pickFirst(stone, ['polish']) || 'N/A';
    var fluorescence = pickFirst(stone, ['fluorescence']) || '';
    var rows = [
      { label: 'Carat Weight', value: (stone.carat != null && stone.carat !== '') ? String(stone.carat) : (stone.weight != null ? String(stone.weight) : 'N/A') },
      { label: 'Color', value: pickFirst(stone, ['color']) || 'N/A' },
      { label: 'Clarity', value: pickFirst(stone, ['clarity']) || 'N/A' },
      { label: 'Cut Grade', value: cutGrade || 'N/A' },
      { label: 'Polish', value: polish || 'N/A' },
      { label: 'Symmetry', value: symmetry || 'N/A' }
    ];
    if (fluorescence) rows.push({ label: 'Fluorescence', value: fluorescence });
    return rows;
  }

  function ensurePreviewModal() {
    var existing = document.getElementById('mdc-diamond-preview-overlay');
    if (existing) return existing;

    var overlay = document.createElement('div');
    overlay.id = 'mdc-diamond-preview-overlay';
    overlay.className = 'mdc-preview-overlay';
    overlay.innerHTML = [
      '<div class="mdc-preview-modal" role="dialog" aria-modal="true" aria-labelledby="mdc-preview-title">',
      '<button type="button" class="mdc-preview-close" data-preview-close aria-label="Close preview">&times;</button>',
      '<div class="mdc-preview-toplabel">Laboratory Grown</div>',
      '<h2 class="mdc-preview-title" id="mdc-preview-title">Diamond</h2>',
      '<div class="mdc-preview-id" data-preview-id></div>',
      '<div class="mdc-preview-price" data-preview-price></div>',
      '<div class="mdc-preview-price-label">Price</div>',
      '<div class="mdc-preview-actions">',
      '<a class="mdc-preview-btn" data-preview-video target="_blank" rel="noopener noreferrer">Play Video</a>',
      '<a class="mdc-preview-btn" data-preview-cert target="_blank" rel="noopener noreferrer">Download Certificate</a>',
      '</div>',
      '<div class="mdc-preview-specs" data-preview-specs></div>',
      '</div>'
    ].join('');
    document.body.appendChild(overlay);

    overlay.addEventListener('click', function(e) {
      if (e.target === overlay || e.target.closest('[data-preview-close]')) {
        closePreviewModal();
      }
    });

    return overlay;
  }

  function setActionLink(el, href) {
    if (!el) return;
    if (href) {
      el.setAttribute('href', href);
      el.setAttribute('aria-disabled', 'false');
    } else {
      el.removeAttribute('href');
      el.setAttribute('aria-disabled', 'true');
    }
  }

  function openPreviewModal(stone, productShapeKey, productCertUrl) {
    if (!stone) return;
    var overlay = ensurePreviewModal();
    if (!overlay) return;

    var titleEl = overlay.querySelector('#mdc-preview-title');
    var idEl = overlay.querySelector('[data-preview-id]');
    var priceEl = overlay.querySelector('[data-preview-price]');
    var specsEl = overlay.querySelector('[data-preview-specs]');
    var videoEl = overlay.querySelector('[data-preview-video]');
    var certEl = overlay.querySelector('[data-preview-cert]');

    var title = toShapeName(stone, productShapeKey);
    var idVal = pickFirst(stone, ['id', 'stoneId', 'stone_id']) || 'N/A';
    var price = (stone.price != null && stone.price !== '') ? fmtDollars(stone.price) : 'N/A';
    var videoUrl = buildViewerUrl(stone);
    var certUrl = buildCertificateUrl(stone, productCertUrl);

    if (titleEl) titleEl.textContent = title;
    if (idEl) idEl.textContent = 'ID: ' + String(idVal);
    if (priceEl) priceEl.textContent = price;

    var rows = getPreviewSpecRows(stone);
    if (specsEl) {
      specsEl.innerHTML = rows.map(function(r) {
        return '<div class="mdc-preview-spec">' +
          '<div class="mdc-preview-spec-value">' + escHtml(r.value) + '</div>' +
          '<div class="mdc-preview-spec-label">' + escHtml(r.label) + '</div>' +
        '</div>';
      }).join('');
    }

    setActionLink(videoEl, videoUrl);
    setActionLink(certEl, certUrl);

    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closePreviewModal() {
    var overlay = document.getElementById('mdc-diamond-preview-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function buildNoneCardHTML(state) {
    var sel = !!(state && state.noDiamond);
    return '<button type="button" class="mdc-diamond-card mdc-diamond-card--none' + (sel ? ' is-selected' : '') + '"' +
      ' data-diamond-none>' +
      '<div class="mdc-diamond-img-wrap">' + NONE_ICON_SVG + '</div>' +
      '<div class="mdc-diamond-info">Setting Only' +
      '<span class="mdc-diamond-price">No diamond</span></div>' +
      '</button>';
  }

  function buildDiamondCardsHTML(state, diamonds) {
    var stones = flattenDiamonds(diamonds);
    var noneCard = buildNoneCardHTML(state);

    if (!stones.length) {
      return { html: noneCard, count: 1 };
    }

    var stoneCards = stones.map(function(stone) {
      var sel = state.diamond && state.diamond.id === stone.id;
      var caratVal = stone.carat != null ? stone.carat : stone.weight;
      var imgUrl = stone.image || (Array.isArray(stone.images) && stone.images[0]) || '';
      var img = imgUrl
        ? '<img src="' + escAttr(imgUrl) + '" alt="' + escAttr(String(caratVal)) + 'ct" loading="lazy" width="74" height="74">'
        : DIAMOND_PLACEHOLDER_SVG;
      return '<button type="button" class="mdc-diamond-card' + (sel ? ' is-selected' : '') + '"' +
        ' data-diamond-id="' + escAttr(stone.id) + '"' +
        ' data-diamond-json=\'' + JSON.stringify(stone).replace(/'/g, '&#39;') + '\'>' +
        '<div class="mdc-diamond-img-wrap">' + img + '</div>' +
        '<div class="mdc-diamond-info">' + caratVal + 'ct | ' + stone.color + '-' + stone.clarity +
        '<span class="mdc-diamond-price">' + fmtDollars(stone.price) + '</span></div>' +
        '<span class="mdc-diamond-preview" data-preview-trigger>' + EYE_SVG + ' Preview</span>' +
        '</button>';
    });

    var allCards = [noneCard].concat(stoneCards);

    return {
      html: allCards.join(''),
      count: allCards.length
    };
  }

  /* ======================================================================
   * v2.11.0 — Paloma-style stacked dropdown field builders
   * ====================================================================== */

  // Shared field wrapper: uppercase label above a full-width control.
  function mdcField(label, controlHTML, sectionName) {
    var attr = sectionName ? ' data-section="' + sectionName + '"' : '';
    return '<div class="mdc-section mdc-field"' + attr + '>' +
      '<span class="mdc-field-label">' + escHtml(label) + '</span>' +
      controlHTML +
    '</div>';
  }

  // SHAPE — navigates between shape-sibling products of the same style.
  function buildShapeDropdownHTML(meta, currentShapeKey) {
    var map = meta && meta.shapeSiblings ? meta.shapeSiblings : {};
    var curKey = normalizeShapeKey(currentShapeKey) || String(currentShapeKey || '').toLowerCase();

    // Normalize the sibling map to lowercase shape keys.
    var norm = {};
    Object.keys(map).forEach(function(k) {
      var nk = normalizeShapeKey(k) || String(k).toLowerCase();
      if (nk && !norm[nk]) norm[nk] = map[k];
    });

    var opts = [];
    var seen = {};

    // Current shape is always first, value-less (selected, no navigation).
    opts.push('<option value="" selected>' + escHtml(shapeDisplayName(curKey)) + '</option>');
    seen[curKey] = 1;

    SHAPE_ORDER.forEach(function(nk) {
      if (norm[nk] && !seen[nk]) {
        seen[nk] = 1;
        opts.push('<option value="' + escAttr(norm[nk]) + '">' + escHtml(shapeDisplayName(nk)) + '</option>');
      }
    });
    Object.keys(norm).forEach(function(nk) {
      if (!seen[nk]) {
        seen[nk] = 1;
        opts.push('<option value="' + escAttr(norm[nk]) + '">' + escHtml(shapeDisplayName(nk)) + '</option>');
      }
    });

    return mdcField('Shape',
      '<select class="mdc-select" data-mdc-nav>' + opts.join('') + '</select>',
      'shape');
  }

  // METAL — full metal names retained (14K/18K distinction preserved).
  function buildMetalDropdownHTML(currentParsed, idx) {
    if (!currentParsed || !idx.metals.length) return '';
    var opts = idx.metals.map(function(m) {
      var isCur = (m === currentParsed.metal);
      if (isCur) return '<option value="" selected>' + escHtml(m) + '</option>';
      var url = urlForMetal(m, currentParsed.carat, idx);
      if (!url) return '<option value="" disabled>' + escHtml(m) + ' \u2014 unavailable</option>';
      return '<option value="' + escAttr(url) + '">' + escHtml(m) + '</option>';
    }).join('');
    return mdcField('Metal',
      '<select class="mdc-select" data-mdc-nav>' + opts + '</select>',
      'metal');
  }

  // CENTRE CARAT SIZE — same range model, unavailable ranges disabled.
  /* v2.14.0 — display-only label for a carat range: "1.50 - 1.99 ct" -> "1.5ct",
   * "2.00+ ct" -> "2ct". Values (URLs) are untouched, so navigation is unchanged. */
  function caratDisplayLabel(caratStr) {
    var m = String(caratStr == null ? '' : caratStr).match(/(\d+(?:\.\d+)?)/);
    if (!m) return String(caratStr || '');
    var n = parseFloat(m[1]);
    if (isNaN(n)) return String(caratStr || '');
    var num = (n % 1 === 0) ? String(n) : String(parseFloat(n.toFixed(2)));
    return num + 'ct';
  }

  function buildCaratDropdownHTML(currentParsed, idx) {
    if (!currentParsed || !idx.carats.length) return '';
    var opts = idx.carats.map(function(c) {
      var lbl = caratDisplayLabel(c);
      var isCur = (c === currentParsed.carat);
      if (isCur) return '<option value="" selected>' + escHtml(lbl) + '</option>';
      var url = urlForCarat(c, currentParsed.metal, idx);
      if (!url) return '<option value="" disabled>' + escHtml(lbl) + ' \u2014 unavailable</option>';
      return '<option value="' + escAttr(url) + '">' + escHtml(lbl) + '</option>';
    }).join('');
    return mdcField('Center Carat Size',
      '<select class="mdc-select" data-mdc-nav>' + opts + '</select>',
      'carat');
  }

  /* ---------- v2.15 native variants (same dropdown chrome, no sibling URLs) ---------- */
  function optionNames(product) {
    return (product.options || []).map(function(o) {
      return typeof o === 'string' ? o : (o && o.name) || '';
    });
  }

  function isNativeCatalog(product) {
    if (!product || !product.variants || !product.variants.length) return false;
    var names = optionNames(product).map(function(n) { return String(n || '').toLowerCase(); });
    var hasMetal = names.some(function(n) { return n.indexOf('metal') !== -1; });
    var hasShape = names.some(function(n) { return n.indexOf('shape') !== -1; });
    if (hasMetal && hasShape) return true;
    var v0 = product.variants[0];
    if (!v0 || !v0.option1 || !v0.option2) return false;
    var blob = String(v0.option1) + ' ' + String(v0.option2) + ' ' + String(v0.option3 || '');
    blob = blob.toLowerCase();
    var looksMetal = /gold|platinum|white|yellow|rose/.test(blob);
    var looksShape = /round|oval|emerald|pear|princess|radiant|cushion|marquise/.test(blob);
    return looksMetal && looksShape;
  }

  function uniqOption(variants, key) {
    var seen = {};
    var out = [];
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i][key];
      if (v && !seen[v]) { seen[v] = 1; out.push(v); }
    }
    return out;
  }

  function optionEquals(a, b) {
    return String(a == null ? '' : a).replace(/\s+/g, ' ').trim().toLowerCase() ===
           String(b == null ? '' : b).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function catalogOptionMap(product) {
    var names = optionNames(product);
    var map = { metal: 1, shape: 2, carat: 3 };
    for (var i = 0; i < names.length; i++) {
      var n = String(names[i] || '').toLowerCase();
      if (n.indexOf('metal') !== -1) map.metal = i + 1;
      else if (n.indexOf('shape') !== -1) map.shape = i + 1;
      else if (n.indexOf('carat') !== -1) map.carat = i + 1;
    }
    return map;
  }

  function optN(v, n) {
    if (!v) return '';
    if (n === 1) return v.option1 || '';
    if (n === 2) return v.option2 || '';
    if (n === 3) return v.option3 || '';
    return '';
  }

  function variantMetal(v, map) {
    map = map || window._mdcOptionMap || { metal: 1, shape: 2, carat: 3 };
    return optN(v, map.metal);
  }
  function variantShape(v, map) {
    map = map || window._mdcOptionMap || { metal: 1, shape: 2, carat: 3 };
    return optN(v, map.shape);
  }
  function variantCarat(v, map) {
    map = map || window._mdcOptionMap || { metal: 1, shape: 2, carat: 3 };
    return optN(v, map.carat);
  }

  function setSelectValue(select, value) {
    if (!select) return;
    var opts = select.options;
    for (var i = 0; i < opts.length; i++) {
      if (optionEquals(opts[i].value, value)) {
        select.selectedIndex = i;
        return;
      }
    }
  }

  function findNativeVariant(variants, metal, shape, carat, map) {
    map = map || window._mdcOptionMap || { metal: 1, shape: 2, carat: 3 };
    var i, v;
    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      if (optionEquals(variantMetal(v, map), metal) &&
          optionEquals(variantShape(v, map), shape) &&
          optionEquals(variantCarat(v, map), carat)) return v;
    }
    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      if (optionEquals(variantMetal(v, map), metal) &&
          optionEquals(variantShape(v, map), shape)) return v;
    }
    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      if (optionEquals(variantMetal(v, map), metal) &&
          optionEquals(variantCarat(v, map), carat)) return v;
    }
    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      if (optionEquals(variantMetal(v, map), metal)) return v;
    }
    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      if (optionEquals(variantShape(v, map), shape) &&
          optionEquals(variantCarat(v, map), carat)) return v;
    }
    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      if (optionEquals(variantShape(v, map), shape)) return v;
    }
    return variants[0] || null;
  }

  /* Client default (Jay): Yellow Gold + 2ct Round. If no Round, use the only shape. */
  function isYellowGoldMetal(metal) {
    return metal && String(metal).toLowerCase().indexOf('yellow') !== -1;
  }

  function caratIs2ct(carat) {
    var c = String(carat || '');
    return c.indexOf('2.00') !== -1 || /^2(\.0|$)/.test(c.trim());
  }

  function uniqMappedOption(variants, field) {
    var seen = {};
    var out = [];
    for (var i = 0; i < variants.length; i++) {
      var v = field === 'metal' ? variantMetal(variants[i])
            : field === 'shape' ? variantShape(variants[i])
            : variantCarat(variants[i]);
      if (v && !seen[v]) { seen[v] = 1; out.push(v); }
    }
    return out;
  }

  function resolveDefaultDisplayVariant(variants) {
    if (!variants || !variants.length) return null;
    var shapes = uniqMappedOption(variants, 'shape');
    var targetShape = shapes[0];
    var si;
    for (si = 0; si < shapes.length; si++) {
      if (String(shapes[si]).toLowerCase() === 'round') {
        targetShape = shapes[si];
        break;
      }
    }

    var yellowMetal = '';
    for (si = 0; si < variants.length; si++) {
      var m = variantMetal(variants[si]);
      if (optionEquals(m, '14K Yellow Gold')) {
        yellowMetal = m;
        break;
      }
    }
    if (!yellowMetal) {
      for (si = 0; si < variants.length; si++) {
        var ym = variantMetal(variants[si]);
        if (isYellowGoldMetal(ym)) {
          yellowMetal = ym;
          break;
        }
      }
    }

    var carat2 = '';
    for (si = 0; si < variants.length; si++) {
      var c = variantCarat(variants[si]);
      if (caratIs2ct(c)) {
        carat2 = c;
        break;
      }
    }

    var match = findNativeVariant(variants, yellowMetal, targetShape, carat2);
    if (match) return match;
    match = findNativeVariant(variants, yellowMetal, targetShape, null);
    if (match) return match;
    return findNativeVariant(variants, null, targetShape, null) || variants[0];
  }

  function buildNativeSelect(label, section, values, current, displayFn) {
    var opts = values.map(function(val) {
      var lbl = displayFn ? displayFn(val) : val;
      var sel = optionEquals(val, current) ? ' selected' : '';
      return '<option value="' + escAttr(val) + '"' + sel + '>' + escHtml(lbl) + '</option>';
    }).join('');
    return mdcField(label,
      '<select class="mdc-select" data-mdc-opt="' + section + '">' + opts + '</select>',
      section);
  }

  function skuShapeMetal(sku) {
    var s = String(sku || '');
    var m = s.match(/-L-([A-Z]+)\d*-([0-9A-Z]+)/i);
    if (m) return { shape: m[1].toUpperCase(), metal: m[2].toUpperCase() };
    m = s.match(/\b(RB|RD|OV|EM|RA|PR|PE|MQ|CU|AS|HT)(\d*)-([0-9A-Z]+)\b/i);
    if (m) return { shape: m[1].toUpperCase(), metal: m[3].toUpperCase() };
    return null;
  }

  function mediaBlob(el) {
    if (!el) return '';
    var parts = [
      el.getAttribute && el.getAttribute('alt'),
      el.getAttribute && el.getAttribute('src'),
      el.getAttribute && el.getAttribute('srcset'),
      el.getAttribute && el.getAttribute('aria-label'),
      el.getAttribute && el.getAttribute('data-max-resolution'),
      el.currentSrc
    ];
    return parts.join(' ').toUpperCase();
  }

  function imageStem(url) {
    if (!url) return '';
    var file = String(url).split('?')[0].split('#')[0].split('/').pop();
    // Shopify size suffix (_800x) + file-UUID suffix (_c3acdc53-81e5-...)
    file = file.replace(/_\d+x(?=\.)/i, '');
    file = file.replace(/_[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}(?=\.)/i, '');
    return file.replace(/\.[a-z0-9]+$/i, '').toLowerCase();
  }

  /* Prefer matching by filename look (R1001-L-RB100-4Y) — ProductImage ids
   * from /products/*.js do NOT equal MediaImage data-media-id in Horizon. */
  function imageLookKey(urlOrBlob) {
    var s = String(urlOrBlob || '').toUpperCase();
    var m = s.match(/R\d+-L-(RB|RD|OV|EM|RA|PR|PE|MQ|CU|AS|HT)\d*-(4[YWRP]|8[YWR]|4P)/i);
    if (m) return m[0].toUpperCase().replace(/-RD/, '-RB');
    return imageStem(urlOrBlob);
  }

  function stemsMatch(a, b) {
    if (!a || !b) return false;
    if (a === b) return true;
    var ka = imageLookKey(a);
    var kb = imageLookKey(b);
    return !!(ka && kb && ka === kb);
  }

  function featuredImageSrc(variant) {
    if (!variant) return '';
    if (variant.featured_media_src) return variant.featured_media_src;
    var fi = variant.featured_image;
    if (!fi) return '';
    if (typeof fi === 'string') return fi;
    return fi.src || fi.url || '';
  }

  function featuredMediaId(variant) {
    if (!variant) return '';
    var id = variant.featured_media_id ||
      (variant.featured_image && typeof variant.featured_image === 'object' && variant.featured_image.id) ||
      (variant.featured_media && variant.featured_media.id);
    return id ? String(id) : '';
  }

  function shapeLookCodes(shapeStr) {
    var k = normalizeShapeKey(shapeStr);
    var map = {
      round: ['RB', 'RD', 'ROUND'],
      oval: ['OV', 'OVAL'],
      emerald: ['EM', 'EMERALD'],
      pear: ['PE', 'PEAR'],
      princess: ['PR', 'PRINCESS'],
      radiant: ['RA', 'RADIANT'],
      marquise: ['MQ', 'MARQUISE'],
      cushion: ['CU', 'CUSHION'],
      asscher: ['AS', 'ASSCHER'],
      heart: ['HT', 'HEART']
    };
    if (map[k]) return map[k];
    var raw = String(shapeStr || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    return raw ? [raw] : [];
  }

  function metalLook(metalStr) {
    var u = String(metalStr || '').toUpperCase().replace(/\s+/g, ' ').trim();
    var family = '';
    if (/\bPLAT/.test(u)) family = 'platinum';
    else if (/\bWHITE/.test(u)) family = 'white';
    else if (/\bYELLOW/.test(u)) family = 'yellow';
    else if (/\bROSE|\bPINK/.test(u)) family = 'rose';
    var karat14 = /\b14\s*K/.test(u);
    var karat18 = /\b18\s*K/.test(u);
    var codes = [];
    if (family === 'white') {
      codes.push('WHITE GOLD', 'WHITE', 'WG');
      if (karat18) codes.push('8W', '18KW', '18KWG', '18W');
      else codes.push('4W', '14KW', '14KWG', '14W');
      if (!karat14 && !karat18) codes.push('4W', '8W', '14KW', '18KW');
    } else if (family === 'yellow') {
      codes.push('YELLOW GOLD', 'YELLOW', 'YG');
      if (karat18) codes.push('8Y', '18KY', '18KYG', '18Y');
      else codes.push('4Y', '14KY', '14KYG', '14Y');
      if (!karat14 && !karat18) codes.push('4Y', '8Y', '14KY', '18KY');
    } else if (family === 'rose') {
      codes.push('ROSE GOLD', 'ROSE', 'RG', 'PINK');
      if (karat18) codes.push('8R', '18KR', '18KRG', '18R');
      else codes.push('4R', '14KR', '14KRG', '14R');
      if (!karat14 && !karat18) codes.push('4R', '8R', '14KR', '18KR');
    } else if (family === 'platinum') {
      codes.push('PLATINUM', 'PLAT', 'PT', '4P');
    }
    if (u) codes.push(u);
    return { family: family, codes: codes };
  }

  function blobHasToken(blob, token) {
    if (!blob || !token) return false;
    var t = String(token).toUpperCase();
    if (t.indexOf(' ') !== -1) return blob.indexOf(t) !== -1;
    if (t.length <= 3) {
      return new RegExp('(?:^|[^A-Z0-9])' + t + '(?:[^A-Z0-9]|$)').test(blob);
    }
    return new RegExp('\\b' + t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b').test(blob);
  }

  function mediaHasShape(blob, codes) {
    if (!blob || !codes || !codes.length) return false;
    for (var i = 0; i < codes.length; i++) {
      var c = codes[i];
      if (blobHasToken(blob, c)) return true;
      if (c.length <= 3 && new RegExp('-' + c + '\\d*-').test(blob)) return true;
    }
    return false;
  }

  function mediaHasMetal(blob, metal) {
    if (!blob || !metal) return false;
    var i;
    if (metal.skuCode && blobHasToken(blob, metal.skuCode)) return true;
    if (metal.skuCode && new RegExp('-' + metal.skuCode + '(?:[-._]|$)').test(blob)) return true;
    for (i = 0; i < (metal.codes || []).length; i++) {
      if (blobHasToken(blob, metal.codes[i])) return true;
      if (metal.codes[i].length <= 4 && new RegExp('-' + metal.codes[i] + '(?:[-._]|$)').test(blob)) return true;
    }
    return false;
  }

  function mediaContradictsShape(blob, shapeStr) {
    if (!blob || !shapeStr) return false;
    var selected = normalizeShapeKey(shapeStr);
    if (!selected) return false;
    var keys = ['round', 'oval', 'emerald', 'pear', 'princess', 'radiant', 'marquise', 'cushion', 'asscher', 'heart'];
    for (var i = 0; i < keys.length; i++) {
      if (keys[i] === selected) continue;
      if (mediaHasShape(blob, shapeLookCodes(keys[i]))) return true;
    }
    return false;
  }

  function mediaContradictsMetal(blob, family) {
    if (!blob || !family) return false;
    var hasY = /\bYELLOW\s+GOLD\b/.test(blob) || /(?:^|[^A-Z0-9])4Y(?:[^A-Z0-9]|$)/.test(blob) || /(?:^|[^A-Z0-9])8Y(?:[^A-Z0-9]|$)/.test(blob);
    var hasW = /\bWHITE\s+GOLD\b/.test(blob) || /(?:^|[^A-Z0-9])4W(?:[^A-Z0-9]|$)/.test(blob) || /(?:^|[^A-Z0-9])8W(?:[^A-Z0-9]|$)/.test(blob);
    var hasR = /\bROSE\s+GOLD\b/.test(blob) || /(?:^|[^A-Z0-9])4R(?:[^A-Z0-9]|$)/.test(blob) || /(?:^|[^A-Z0-9])8R(?:[^A-Z0-9]|$)/.test(blob);
    if (family === 'white') return hasY || hasR;
    if (family === 'yellow') return hasW || hasR;
    if (family === 'rose') return hasW || hasY;
    if (family === 'platinum') return hasY || hasR;
    return false;
  }

  function mediaMatchesLook(blob, look) {
    if (!blob || !look) return false;
    if (look.featuredStem && blob.toLowerCase().indexOf(look.featuredStem) !== -1) {
      if (!mediaContradictsMetal(blob, look.metal && look.metal.family)) return true;
    }
    if (look.featuredStem && stemsMatch(imageStem(blob), look.featuredStem)) {
      if (!mediaContradictsMetal(blob, look.metal && look.metal.family)) return true;
    }
    var shapeOk = !look.shapeCodes.length || mediaHasShape(blob, look.shapeCodes);
    var metalOk = mediaHasMetal(blob, look.metal);
    if (mediaContradictsMetal(blob, look.metal && look.metal.family)) return false;
    return shapeOk && metalOk;
  }

  function mediaLooksLikeStock(blob) {
    if (!blob) return false;
    if (/\b(RB|RD|OV|EM|RA|PR|PE|MQ|CU|AS|HT)\s+(?:14K|18K|4[YWRP]|8[YWR])\b/.test(blob)) return true;
    if (/-L-(RB|RD|OV|EM|RA|PR|PE|MQ|CU|AS|HT)\d*-[0-9A-Z]+(?:[-._]|$)/.test(blob)) return true;
    if (/\b(WHITE|YELLOW|ROSE)\s+GOLD\b/.test(blob)) return true;
    if (/\b(RB|RD|OV|EM|RA|PR|PE|MQ)\d*[-_](?:4[YWR]|8[YWR]|14K[WYR]|18K[WYR]|WG|YG|RG)/.test(blob)) return true;
    return false;
  }

  function lookFromVariant(variant) {
    var skuParts = skuShapeMetal(variant && variant.sku);
    var metalStr = variantMetal(variant);
    var shapeStr = variantShape(variant);
    var metal = metalLook(metalStr);
    if (skuParts && skuParts.metal) metal.skuCode = skuParts.metal;
    var shapeCodes = shapeLookCodes(shapeStr);
    if (skuParts && skuParts.shape && shapeCodes.indexOf(skuParts.shape) === -1) {
      shapeCodes = [skuParts.shape].concat(shapeCodes);
    }
    return {
      metal: metal,
      shapeCodes: shapeCodes,
      featuredStem: imageStem(featuredImageSrc(variant)),
      sku: variant && variant.sku
    };
  }

  function lookMediaIds(variant, variants) {
    var ids = {};
    var metal = variantMetal(variant);
    var shape = variantShape(variant);
    (variants || []).forEach(function(v) {
      if (!optionEquals(variantMetal(v), metal)) return;
      if (shape && !optionEquals(variantShape(v), shape)) return;
      var id = featuredMediaId(v);
      if (id) ids[id] = 1;
    });
    var selfId = featuredMediaId(variant);
    if (selfId) ids[selfId] = 1;
    return ids;
  }

  function otherVariantMediaIds(variant, variants) {
    var keep = lookMediaIds(variant, variants);
    var others = {};
    (variants || []).forEach(function(v) {
      var id = featuredMediaId(v);
      if (id && !keep[id]) others[id] = 1;
    });
    return others;
  }

  function applyImageSrc(img, url) {
    if (!img || !url) return;
    img.srcset = '';
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = url;
    var pic = img.closest && img.closest('picture');
    if (pic) {
      pic.querySelectorAll('source').forEach(function(s) { s.removeAttribute('srcset'); });
    }
  }

  function lookHideStems(variant) {
    var stems = {};
    var keep = {};
    var metal = variantMetal(variant);
    var shape = variantShape(variant);
    var variants = (window._mdcNativeProduct && window._mdcNativeProduct.variants) || [];
    variants.forEach(function(v) {
      var stem = imageStem(featuredImageSrc(v));
      if (!stem) return;
      if (optionEquals(variantMetal(v), metal) && optionEquals(variantShape(v), shape)) keep[stem] = 1;
      else stems[stem] = 1;
    });
    Object.keys(keep).forEach(function(s) { delete stems[s]; });
    return stems;
  }

  function isWrongLookMedia(img, variant, hideStems) {
    if (!img || !variant) return false;
    var family = (metalLook(variantMetal(variant)) || {}).family;
    var blob = mediaBlob(img);
    if (mediaContradictsMetal(blob, family)) return true;
    if (mediaContradictsShape(blob, variantShape(variant))) return true;
    var stem = imageStem(img.currentSrc || img.getAttribute('src') || '');
    hideStems = hideStems || lookHideStems(variant);
    if (stem && hideStems[stem]) return true;
    return false;
  }

  function hideWrongLookGallery(variant) {
    if (!variant) return;
    var hideStems = lookHideStems(variant);
    document.querySelectorAll(
      'media-gallery slideshow-slides > slideshow-slide img, media-gallery slideshow-slide img'
    ).forEach(function(img) {
      if (isWrongLookMedia(img, variant, hideStems)) hideMediaNode(img, true);
    });
  }

  function setControlHidden(el, hide) {
    if (!el) return;
    if (hide) {
      el.classList.add('mdc-media-hide');
      el.setAttribute('hidden', '');
      el.style.setProperty('display', 'none', 'important');
    } else {
      el.classList.remove('mdc-media-hide');
      el.removeAttribute('hidden');
      el.style.removeProperty('display');
    }
  }

  function hideNativeVariantChrome() {
    document.querySelectorAll('variant-picker fieldset, variant-picker .variant-option').forEach(function(el) {
      el.style.display = 'none';
    });
  }

  function optionWantedForLabel(label, variant) {
    var blob = String(label || '').toLowerCase();
    if (blob.indexOf('metal') !== -1) return variantMetal(variant);
    if (blob.indexOf('shape') !== -1) return variantShape(variant);
    if (blob.indexOf('carat') !== -1) return variantCarat(variant);
    return '';
  }

  function syncHorizonVariantPicker(variant) {
    if (!variant || window._mdcHorizonSyncing) return;
    var picker = document.querySelector('variant-picker[data-product-id], variant-picker');
    if (!picker) return;
    var lastChanged = null;
    var map = window._mdcOptionMap || { metal: 1, shape: 2, carat: 3 };

    picker.querySelectorAll('fieldset').forEach(function(fs, idx) {
      var legend = fs.querySelector('legend');
      var want = optionWantedForLabel(legend ? legend.textContent : '', variant);
      if (!want) {
        if (idx + 1 === map.metal) want = variantMetal(variant);
        else if (idx + 1 === map.shape) want = variantShape(variant);
        else if (idx + 1 === map.carat) want = variantCarat(variant);
      }
      if (!want) return;
      var inputs = fs.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < inputs.length; i++) {
        var inp = inputs[i];
        if (optionEquals(inp.value, want) || optionEquals(inp.getAttribute('aria-label'), want)) {
          if (!inp.checked) {
            inp.checked = true;
            lastChanged = inp;
          }
          break;
        }
      }
    });

    picker.querySelectorAll('select').forEach(function(sel) {
      var labelEl = sel.id ? document.querySelector('label[for="' + sel.id + '"]') : null;
      var want = optionWantedForLabel((labelEl && labelEl.textContent) || sel.name || '', variant);
      if (!want) return;
      var before = sel.value;
      setSelectValue(sel, want);
      if (sel.value !== before) lastChanged = sel;
    });

    if (!lastChanged) return;
    window._mdcHorizonSyncing = true;
    try { lastChanged.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
    setTimeout(function() { window._mdcHorizonSyncing = false; }, 2000);
  }

  function applyVariantMedia(variant) {
    if (!variant) return 0;
    window._mdcGalleryVariant = variant;
    window._mdcGalleryFiltering = true;
    var shown = 0;
    try {
      var look = lookFromVariant(variant);
      var featuredSrc = featuredImageSrc(variant);
      var featuredStem = imageStem(featuredSrc);
      var featuredKey = imageLookKey(featuredSrc);
      var imgs = document.querySelectorAll(
        'media-gallery slideshow-slide img, media-gallery .product-media-container img, slideshow-slide img'
      );
      var firstShown = null;
      var featuredImg = null;

      imgs.forEach(function(img) {
        var blob = mediaBlob(img);
        var src = img.currentSrc || img.getAttribute('src') || '';
        var stem = imageStem(src);
        var isStock = mediaLooksLikeStock(blob);
        var isFeatured = !!(featuredStem && stemsMatch(stem, featuredStem)) ||
          !!(featuredKey && imageLookKey(src) === featuredKey);
        var keep = isFeatured || mediaMatchesLook(blob, look);

        if (!isStock && !isFeatured) {
          // Non-stock / lifestyle: leave alone
          return;
        }
        if (keep) {
          hideMediaNode(img, false);
          if (!firstShown) firstShown = img;
          if (isFeatured) featuredImg = img;
          shown++;
        } else {
          hideMediaNode(img, true);
        }
      });

      if (!shown) {
        shown = filterGalleryToLook(look, variant.sku);
      } else {
        hideWrongLookGallery(variant);
        syncGalleryControls(featuredImg || firstShown);
      }
    } finally {
      window._mdcGalleryFiltering = false;
    }
    return shown;
  }

  function hideMediaNode(el, hide) {
    var slide = el && el.closest && el.closest('slideshow-slide');
    if (slide) {
      // Native Horizon: [hidden] drops slide from slideshow.slides.
      // CSS above also removes it from the scroll track (desktop + mobile).
      if (hide) {
        slide.setAttribute('hidden', '');
        slide.removeAttribute('reveal');
        slide.classList.add('mdc-media-hide');
        slide.setAttribute('inert', '');
        slide.setAttribute('aria-hidden', 'true');
        slide.style.removeProperty('display');
      } else {
        slide.removeAttribute('hidden');
        slide.removeAttribute('reveal');
        slide.classList.remove('mdc-media-hide');
        slide.removeAttribute('inert');
        slide.style.removeProperty('display');
      }
      return slide;
    }

    var wrap = el.closest('.product-media-container') ||
      el.closest('li.product-media-container') ||
      el.closest('li');
    var target = wrap || el;
    if (target.matches && (target.matches('media-gallery') || target.matches('slideshow-component') || target.matches('ul'))) {
      target = el;
    }
    var already = target.classList.contains('mdc-media-hide');
    if (hide && already) return target;
    if (!hide && !already) return target;
    if (hide) {
      target.classList.add('mdc-media-hide');
      target.setAttribute('hidden', '');
      target.style.setProperty('display', 'none', 'important');
    } else {
      target.classList.remove('mdc-media-hide');
      target.removeAttribute('hidden');
      target.style.removeProperty('display');
    }
    return target;
  }

  function snapGalleryScroller(ss, scroller, targetSlide) {
    var track = scroller ||
      (ss && ss.refs && ss.refs.scroller) ||
      (ss && ss.querySelector && ss.querySelector('slideshow-slides'));
    if (!track) return;

    var go = function() {
      if (targetSlide && targetSlide.isConnected) {
        var left = targetSlide.offsetLeft;
        track.scrollLeft = left < 0 ? 0 : left;
        track.scrollTop = 0;
        return;
      }
      track.scrollLeft = 0;
      track.scrollTop = 0;
    };

    go();
    if (!_mdcMobileMQ.matches) return;
    requestAnimationFrame(function() {
      go();
      requestAnimationFrame(go);
    });
  }

  function syncGalleryControls(preferredImg) {
    var variant = window._mdcGalleryVariant;
    var hideStems = variant ? lookHideStems(variant) : {};

    document.querySelectorAll('media-gallery slideshow-component, slideshow-component').forEach(function(ss) {
      if (ss.closest && ss.closest('.dialog-zoomed-gallery')) return;
      var scroller = ss.querySelector(':scope > slideshow-container > slideshow-slides') ||
                     ss.querySelector('slideshow-slides');
      var slides = scroller
        ? Array.prototype.slice.call(scroller.children).filter(function(el) {
            return el.tagName && el.tagName.toLowerCase() === 'slideshow-slide';
          })
        : Array.prototype.slice.call(ss.querySelectorAll('slideshow-slides > slideshow-slide'));

      var scope = ss.closest('media-gallery') || ss;
      var thumbs = Array.prototype.slice.call(ss.querySelectorAll(
        '[ref="thumbnails[]"], .slideshow-controls__thumbnail'
      ));
      if (!thumbs.length) {
        thumbs = Array.prototype.slice.call(scope.querySelectorAll(
          '[ref="thumbnails[]"], .slideshow-controls__thumbnail'
        ));
      }

      // Absolute index → index among VISIBLE slides (what Horizon select(n) uses)
      var visibleOrder = [];
      slides.forEach(function(slide, idx) {
        if (!slide.hasAttribute('hidden') || slide.hasAttribute('reveal')) {
          visibleOrder.push(idx);
        }
      });

      thumbs.forEach(function(thumb, idx) {
        var slide = slides[idx];
        var thumbImg = thumb.querySelector ? thumb.querySelector('img') : null;
        var hide = !!(slide && slide.hasAttribute('hidden') && !slide.hasAttribute('reveal'));
        if (!hide && variant && thumbImg && isWrongLookMedia(thumbImg, variant, hideStems)) hide = true;
        if (!hide && variant && slide) {
          var slideImg = slide.querySelector('img');
          if (slideImg && isWrongLookMedia(slideImg, variant, hideStems)) hide = true;
        }
        setControlHidden(thumb, hide);
        if (hide) {
          thumb.removeAttribute('data-mdc-visible-index');
        } else {
          thumb.setAttribute('data-mdc-visible-index', String(visibleOrder.indexOf(idx)));
        }
      });

      var dots = [];
      if (ss.refs && ss.refs.dots && ss.refs.dots.length) {
        dots = Array.prototype.slice.call(ss.refs.dots);
      }
      if (!dots.length) {
        dots = Array.prototype.slice.call(scope.querySelectorAll(
          '[ref="dots[]"], .slideshow-controls__dots .slideshow-control, .media-gallery__mobile-controls [ref="dots[]"]'
        ));
      }
      dots.forEach(function(btn, idx) {
        var slide = slides[idx];
        var hide = !slide || (slide.hasAttribute('hidden') && !slide.hasAttribute('reveal'));
        if (!hide && variant && slide) {
          var slideImg = slide.querySelector('img');
          if (slideImg && isWrongLookMedia(slideImg, variant, hideStems)) hide = true;
        }
        var target = btn.closest('li') || btn;
        setControlHidden(target, hide);
        if (hide) {
          btn.removeAttribute('data-mdc-visible-index');
        } else {
          btn.setAttribute('data-mdc-visible-index', String(visibleOrder.indexOf(idx)));
        }
      });

      scope.querySelectorAll('.dialog-thumbnails-list__thumbnail').forEach(function(thumb, idx) {
        var slide = slides[idx];
        var thumbImg = thumb.querySelector ? thumb.querySelector('img') : null;
        var hide = !!(slide && slide.hasAttribute('hidden') && !slide.hasAttribute('reveal'));
        if (!hide && variant && thumbImg && isWrongLookMedia(thumbImg, variant, hideStems)) hide = true;
        setControlHidden(thumb, hide);
        if (hide) {
          thumb.removeAttribute('data-mdc-visible-index');
        } else {
          thumb.setAttribute('data-mdc-visible-index', String(visibleOrder.indexOf(idx)));
        }
      });

      // Patch Horizon select(): thumbs pass ABSOLUTE slide index (e.g. 72) via
      // on:click="/select/72", but select(n) indexes VISIBLE slides only. Without
      // remapping, 72 wraps to 0 → main image never changes.
      if (!ss._mdcSelectPatched && typeof ss.select === 'function') {
        ss._mdcSelectPatched = true;
        var _origSelect = ss.select.bind(ss);
        ss.select = function(input, event, options) {
          var n = null;
          if (typeof input === 'number') n = input;
          else if (typeof input === 'string' && /^\d+$/.test(input)) n = parseInt(input, 10);
          if (n != null && !isNaN(n)) {
            var all = Array.prototype.slice.call(
              (scroller || ss).querySelectorAll(':scope > slideshow-slide')
            );
            if (!all.length) {
              all = Array.prototype.slice.call(ss.querySelectorAll('slideshow-slides > slideshow-slide'));
            }
            var vis = all.filter(function(s) {
              return !s.hasAttribute('hidden') || s.hasAttribute('reveal');
            });
            if (n >= 0 && n < all.length) {
              var absSlide = all[n];
              // Absolute index into ALL slides pointing at a currently-visible slide
              // → remap to visible index. Our own calls pass visible indexes where
              // all[n] is usually hidden, so those stay untouched.
              if (absSlide && vis.indexOf(absSlide) !== -1) {
                var remapped = vis.indexOf(absSlide);
                if (remapped !== n) input = remapped;
              }
            }
            // Remember user pick so syncGalleryControls doesn't snap back to featured
            if (event) {
              var pick = (typeof input === 'number') ? vis[input] : null;
              if (!pick && n < all.length) pick = all[n];
              if (pick) window._mdcUserSlide = pick;
            }
          }
          return _origSelect(input, event, options);
        };
      }

      if (!ss._mdcThumbFix) {
        ss._mdcThumbFix = true;
        ss.addEventListener('click', function(e) {
          var thumb = e.target && e.target.closest && e.target.closest(
            '[ref="thumbnails[]"], .slideshow-controls__thumbnail, .dialog-thumbnails-list__thumbnail'
          );
          if (!thumb || (ss.contains && !ss.contains(thumb))) return;

          var allThumbs = Array.prototype.slice.call(ss.querySelectorAll(
            '[ref="thumbnails[]"], .slideshow-controls__thumbnail'
          ));
          if (!allThumbs.length) {
            allThumbs = Array.prototype.slice.call(
              (ss.closest('media-gallery') || ss).querySelectorAll(
                '[ref="thumbnails[]"], .slideshow-controls__thumbnail, .dialog-thumbnails-list__thumbnail'
              )
            );
          }
          var absIdx = allThumbs.indexOf(thumb);
          if (absIdx < 0) return;
          var slide = slides[absIdx];
          if (!slide) return;

          // Let patched select() remap absolute absIdx → visible
          window._mdcUserSlide = slide;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          try {
            if (typeof ss.select === 'function') ss.select(absIdx, e, { animate: true });
          } catch (err) {}
        }, true);
      }

      var visible = slides.filter(function(s) {
        return !s.hasAttribute('hidden') || s.hasAttribute('reveal');
      });
      var targetSlide = null;
      // Keep user's thumb pick if that slide is still in the filtered set
      if (window._mdcUserSlide && visible.indexOf(window._mdcUserSlide) >= 0) {
        targetSlide = window._mdcUserSlide;
      } else {
        window._mdcUserSlide = null;
        targetSlide = visible[0] || null;
        if (preferredImg && preferredImg.closest) {
          var prefSlide = preferredImg.closest('slideshow-slide');
          if (prefSlide && visible.indexOf(prefSlide) >= 0) targetSlide = prefSlide;
        }
      }
      var start = targetSlide ? visible.indexOf(targetSlide) : 0;
      if (start < 0) start = 0;

      try {
        if (typeof ss.select === 'function') {
          ss.select(start, undefined, { animate: false });
        }
      } catch (e) {}
      snapGalleryScroller(ss, scroller, targetSlide);
    });

    document.querySelectorAll('media-gallery').forEach(function(g) {
      g.classList.add('mdc-gallery-ready');
    });
  }

  function watchGalleryFilter() {
    if (window._mdcGalleryObs) return;
    var root = document.querySelector('media-gallery') || document.body;
    if (!root || !window.MutationObserver) return;
    var timer = null;
    window._mdcGalleryObs = new MutationObserver(function() {
      if (window._mdcGalleryFiltering) return;
      clearTimeout(timer);
      timer = setTimeout(function() {
        if (window._mdcGalleryVariant) applyVariantMedia(window._mdcGalleryVariant);
        else if (window._mdcGallerySku) filterGalleryToSku(window._mdcGallerySku);
      }, 120);
    });
    window._mdcGalleryObs.observe(root, { childList: true, subtree: true });
  }

  function filterGalleryToSku(sku) {
    return filterGalleryToLook({ sku: sku, metal: metalLook(''), shapeCodes: [], featuredStem: '' }, sku);
  }

  function filterGalleryToVariant(variant) {
    if (!variant) return 0;
    window._mdcGalleryVariant = variant;
    window._mdcGallerySku = variant.sku || window._mdcGallerySku;
    return filterGalleryToLook(lookFromVariant(variant), variant.sku);
  }

  function filterGalleryToLook(look, sku) {
    if (!look) return 0;
    if (sku) window._mdcGallerySku = sku;
    if ((!look.shapeCodes || !look.shapeCodes.length) && sku) {
      var parts = skuShapeMetal(sku);
      if (parts) {
        look = look || {};
        look.shapeCodes = [parts.shape];
        look.metal = look.metal || metalLook('');
        look.metal.skuCode = parts.metal;
      }
    }
    if ((!look.shapeCodes || !look.shapeCodes.length) && !(look.metal && (look.metal.family || look.metal.skuCode))) {
      return 0;
    }
    window._mdcGalleryFiltering = true;

    var shown = 0;
    var firstShown = null;
    var stockNodes = [];
    var imgs = document.querySelectorAll(
      'media-gallery slideshow-slide img, media-gallery .product-media-container img, .product-media-gallery img, slideshow-slide img'
    );

    imgs.forEach(function(img) {
      var blob = mediaBlob(img);
      var isStock = mediaLooksLikeStock(blob);
      var stem = imageStem(img.currentSrc || img.getAttribute('src') || '');
      var isFeatured = !!(look.featuredStem && stem && stemsMatch(stem, look.featuredStem));
      if (!isStock && !isFeatured) return;
      stockNodes.push(img);
      if (mediaMatchesLook(blob, look) || (isFeatured && !mediaContradictsMetal(blob, look.metal && look.metal.family))) {
        hideMediaNode(img, false);
        if (!firstShown) firstShown = img;
        shown++;
      } else {
        hideMediaNode(img, true);
      }
    });

    if (shown === 0 && stockNodes.length) {
      var familyKeep = stockNodes.filter(function(img) {
        var blob = mediaBlob(img);
        if (mediaContradictsMetal(blob, look.metal && look.metal.family)) return false;
        return mediaHasMetal(blob, look.metal) ||
          (look.featuredStem && imageStem(img.currentSrc || img.src) === look.featuredStem);
      });
      var keep = familyKeep[0];
      if (keep) {
        hideMediaNode(keep, false);
        firstShown = keep;
        shown = 1;
      }
    }

    syncGalleryControls(firstShown);
    window._mdcGalleryFiltering = false;
    return shown;
  }

  function setCartVariantId(variantId) {
    if (!variantId) return;
    document.querySelectorAll('form[action*="/cart/add"] input[name="id"], input[name="id"][ref="variantId"]').forEach(function(input) {
      input.value = String(variantId);
      try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
    });
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', u.pathname + u.search);
    } catch (e) {}
  }

  function applyNativeVariant(pid, meta, product, variant, state) {
    if (!variant) return;
    state.variant = variant;
    window._mdcGalleryVariant = variant;
    window._mdcUserSlide = null; // new look → reset thumb pick
    setCartVariantId(variant.id);
    var cents = Number(variant.price) || 0;
    updatePriceDisplay(pid, cents, state);
    watchGalleryFilter();
    applyVariantMedia(variant);
    if (!window._mdcHorizonSyncing) syncHorizonVariantPicker(variant);
    [80, 300, 800, 1600].forEach(function(ms) {
      setTimeout(function() { applyVariantMedia(variant); }, ms);
    });
    if (meta.details) {
      meta.details.metal = variantMetal(variant) || meta.details.metal;
      meta.sku = variant.sku || meta.sku;
    }
    refreshDetailsBlock(meta, pid);
  }

  /* v2.14.3 — normalizes a ring-size value for comparison purposes only
   * (e.g. "6.50" and "6.5" and 6.5 all compare equal to the constant "6.5").
   * The raw, unmodified value from meta.ringSize is still what's rendered
   * and submitted — this never mutates the actual size string used elsewhere. */
  function normalizeSizeValue(v) {
    var n = parseFloat(String(v == null ? '' : v).trim());
    return isNaN(n) ? String(v == null ? '' : v).trim() : String(n);
  }

  /* v2.14.3 — resolves RING_SIZE_DEFAULT ('6.5') to whichever exact string is
   * present in this product's ringSizes list (so "6.5", "6.50", etc. all
   * match), or '' if the product simply doesn't offer that size. */
  function resolveDefaultRingSize(ringSizes) {
    if (!ringSizes || !ringSizes.length) return '';
    var target = normalizeSizeValue(RING_SIZE_DEFAULT);
    for (var i = 0; i < ringSizes.length; i++) {
      if (normalizeSizeValue(ringSizes[i]) === target) return ringSizes[i];
    }
    return '';
  }

  // RING SIZE — property injection (no navigation).
  // v2.14.3 — pre-selects RING_SIZE_DEFAULT ('6.5') when the product offers
  // it, and labels that option "<size> (Default)".
  // v2.14.5 — closed select paints "(Default)" via a face overlay so it can
  // match .mdc-price-caption italic (native <option> can't style a substring).
  function syncRingSizeDefaultDisplay(select) {
    if (!select) return;
    var wrap = select.closest('.mdc-ring-size-wrap');
    if (!wrap) return;
    var face = wrap.querySelector('[data-mdc-ring-size-face]');
    var opt = select.options[select.selectedIndex];
    var isDefault = !!(opt && opt.hasAttribute('data-mdc-default-size'));
    wrap.classList.toggle('is-default-selected', isDefault);
    if (!face) return;
    if (isDefault) {
      face.innerHTML =
        '<span class="mdc-ring-size-face__value">' + escHtml(opt.value) + '</span>' +
        '<span class="mdc-ring-size-face__default"> (Default)</span>';
    } else {
      face.textContent = '';
    }
  }

  function buildRingSizeHTML(ringSizes, state) {
    if (!ringSizes || !ringSizes.length) return '';

    var defaultVal      = resolveDefaultRingSize(ringSizes);
    var effectiveSize    = state.size || defaultVal; // fall back to default only if nothing explicitly chosen yet

    var optionsHTML = '<option value="">Select</option>';
    for (var i = 0; i < ringSizes.length; i++) {
      var sVal      = ringSizes[i];
      var isDefault = !!defaultVal && sVal === defaultVal;
      var label     = isDefault ? (sVal + ' (Default)') : sVal;
      var sel       = (effectiveSize === sVal) ? ' selected' : '';
      var defAttr   = isDefault ? ' data-mdc-default-size' : '';
      optionsHTML += '<option value="' + escAttr(sVal) + '"' + sel + defAttr + '>' + escHtml(label) + '</option>';
    }

    // v2.14.0 — shoppers who don't know their size yet.
    var laterSel = (effectiveSize === RING_SIZE_LATER) ? ' selected' : '';
    optionsHTML += '<option value="' + escAttr(RING_SIZE_LATER) + '"' + laterSel + '>' +
                   escHtml(RING_SIZE_LATER) + '</option>';
    return mdcField('Ring Size',
      '<div class="mdc-ring-size-wrap">' +
        '<span class="mdc-ring-size-face" data-mdc-ring-size-face aria-hidden="true"></span>' +
        '<select class="mdc-select" data-mdc-ring-size>' + optionsHTML + '</select>' +
      '</div>',
      'ringsize');
  }

  /* v2.12 — outline shape glyphs for the Compatible Diamond Shapes list */
  function svgWrap(inner) {
    return '<svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" stroke-linecap="round">' + inner + '</svg>';
  }
  var SHAPE_ICON = {
    round:    svgWrap('<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5"/>'),
    oval:     svgWrap('<ellipse cx="12" cy="12" rx="6" ry="9"/>'),
    emerald:  svgWrap('<path d="M8 3h8l3 3v12l-3 3H8l-3-3V6z"/>'),
    asscher:  svgWrap('<path d="M8.5 3h7l5.5 5.5v7L15.5 21h-7L3 15.5v-7z"/>'),
    princess: svgWrap('<rect x="4" y="4" width="16" height="16"/><path d="M4 4l16 16M20 4L4 20"/>'),
    cushion:  svgWrap('<rect x="4" y="4" width="16" height="16" rx="5"/>'),
    radiant:  svgWrap('<path d="M8 3h8l4 4v10l-4 4H8l-4-4V7z"/>'),
    pear:     svgWrap('<path d="M12 3c-4 5-6 8-6 11a6 6 0 0 0 12 0c0-3-2-6-6-11z"/>'),
    marquise: svgWrap('<path d="M12 3c4 5 4 13 0 18C8 16 8 8 12 3z"/>'),
    heart:    svgWrap('<path d="M12 20C6.5 15.5 4 11.5 4 8.2A3.7 3.7 0 0 1 12 6a3.7 3.7 0 0 1 8 2.2c0 3.3-2.5 7.3-8 11.8z"/>'),
    _default: svgWrap('<path d="M8 14h8l-4 7zM8 14l4-7 4 7z"/>')
  };
  function shapeIconSVG(key) {
    var k = normalizeShapeKey(key) || String(key || '').toLowerCase().trim();
    return SHAPE_ICON[k] || SHAPE_ICON._default;
  }
  function displayShapeLabel(s) {
    var t = String(s == null ? '' : s).trim();
    if (!t) return '';
    return t.charAt(0).toUpperCase() + t.slice(1);
  }

  /* v2.13 — copy the theme's heading font (from the product title) so THE
   * DETAILS heading matches other headings instead of the body font. */
  var _mdcHeadingFont = null;
  function getHeadingFontFamily() {
    if (_mdcHeadingFont !== null) return _mdcHeadingFont;
    var sels = ['.product-title', '.product__title', '[class*="product__title"]',
                '[class*="product-title"]', '.product-information__title', 'h1'];
    for (var i = 0; i < sels.length; i++) {
      var el = document.querySelector(sels[i]);
      if (el) {
        try {
          var ff = getComputedStyle(el).fontFamily;
          if (ff) { _mdcHeadingFont = ff; return ff; }
        } catch (e) {}
      }
    }
    _mdcHeadingFont = '';
    return '';
  }

  /* v2.13 — always resolve the live product/variant SKU for Style Number. */
  function getVariantIdFromUrl() {
    try { var m = window.location.search.match(/[?&]variant=(\d+)/); return m ? m[1] : ''; }
    catch (e) { return ''; }
  }
  function resolveSku(meta) {
    if (meta && meta.sku) return String(meta.sku);
    try {
      var p = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      if (p && p.variants && p.variants.length) {
        var vid = getVariantIdFromUrl();
        if (vid) {
          for (var i = 0; i < p.variants.length; i++) {
            if (String(p.variants[i].id) === vid && p.variants[i].sku) return p.variants[i].sku;
          }
        }
        if (p.variants[0].sku) return p.variants[0].sku;
      }
    } catch (e) {}
    var sels = ['[data-product-sku]', '.product__sku', '.variant-sku', '[data-variant-sku]', '.product-sku'];
    for (var j = 0; j < sels.length; j++) {
      var el = document.querySelector(sels[j]);
      if (el) {
        var tx = (el.textContent || '').trim();
        if (tx) return tx.replace(/^SKU:?\s*/i, '');
      }
    }
    return '';
  }

  // THE DETAILS — two-column block rendered in the LEFT column under the media.
  // Left col: Style Number + Compatible Diamond Shapes (icon list).
  // Right col: Color, Clarity, Compatible Carat Range, Metal.
  function buildDetailsHTML(meta, pid) {
    var d = (meta && meta.details) || {};

    // Style Number ALWAYS reflects the live SKU; metafield only as last resort.
    var styleNumber = resolveSku(meta) || d.styleNumber || '';

    var shapes = Array.isArray(d.compatibleShapes) && d.compatibleShapes.length
      ? d.compatibleShapes
      : Object.keys((meta && meta.shapeSiblings) || {});

    var rightRows = [];
    if (d.color)      rightRows.push(['Color', d.color]);
    if (d.clarity)    rightRows.push(['Clarity', d.clarity]);
    if (d.caratRange) rightRows.push(['Compatible Carat Range', d.caratRange]);
    if (d.metal)      rightRows.push(['Metal', d.metal]);

    var hasLeft = !!styleNumber || shapes.length > 0;
    if (!hasLeft && !rightRows.length) return '';

    var leftBlocks = '';
    if (styleNumber) {
      leftBlocks += '<div class="mdc-detail-block">' +
        '<div class="mdc-detail-label">Style Number</div>' +
        '<div class="mdc-detail-value">' + escHtml(styleNumber) + '</div>' +
      '</div>';
    }
    if (shapes.length) {
      var items = shapes.map(function(sh) {
        return '<li class="mdc-shape-item">' + shapeIconSVG(sh) +
          '<span>' + escHtml(displayShapeLabel(sh)) + '</span></li>';
      }).join('');
      leftBlocks += '<div class="mdc-detail-block">' +
        '<div class="mdc-detail-label">Compatible Diamond Shapes</div>' +
        '<ul class="mdc-shape-list">' + items + '</ul>' +
      '</div>';
    }

    var rightBlocks = rightRows.map(function(r) {
      return '<div class="mdc-detail-block">' +
        '<div class="mdc-detail-label">' + escHtml(r[0]) + '</div>' +
        '<div class="mdc-detail-value">' + escHtml(r[1]) + '</div>' +
      '</div>';
    }).join('');

    return '<div class="mdc-details" id="mdc-details-' + pid + '">' +
      '<div class="mdc-details-col mdc-details-col--left">' +
        '<h3 class="mdc-details-title">The Details</h3>' +
        leftBlocks +
      '</div>' +
      (rightBlocks
        ? '<div class="mdc-details-col mdc-details-col--right">' + rightBlocks + '</div>'
        : '') +
    '</div>';
  }

  /* v2.12 — locate the product media column so THE DETAILS can sit under it. */
  function findMediaColumn() {
    var sels = [
      'media-gallery',
      '.product-media-gallery',
      '[class*="product__media-gallery"]',
      '.product__media-wrapper',
      '.product__media-list',
      'slideshow-component',
      '[data-product-media-list]',
      '.product__media'
    ];
    var media = null;
    for (var i = 0; i < sels.length; i++) {
      media = document.querySelector(sels[i]);
      if (media) break;
    }
    if (!media) return null;

    // Climb to the column-level ancestor (a child of the media/info grid),
    // so details append INSIDE the media column rather than after the gallery.
    var el = media;
    for (var j = 0; j < 4 && el.parentElement; j++) {
      if (el.parentElement.children.length >= 2) return el;
      el = el.parentElement;
    }
    return media;
  }

  /* v2.13.3 — identify the product-info column (grid sibling of the media). */
  function findInfoColumn(pid) {
    var anchor = document.getElementById('mdc-wrapper-' + pid) || document.getElementById('mdcp-' + pid);
    if (!anchor) return null;
    var media = findMediaColumn();
    var grid = media && media.parentElement;
    if (!grid) return null;
    for (var i = 0; i < grid.children.length; i++) {
      var child = grid.children[i];
      if (child !== media && child.contains(anchor)) return child;
    }
    return null;
  }

  function isDividerLike(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.tagName.toLowerCase() === 'hr') return true;
    var cls = '';
    try { cls = (typeof el.className === 'string' ? el.className : (el.getAttribute && el.getAttribute('class')) || ''); } catch (e) {}
    if (/divider|separator/i.test(cls)) return true;
    if (el.children.length === 1 && el.firstElementChild && el.firstElementChild.tagName.toLowerCase() === 'hr') return true;
    return false;
  }

  /* v2.13.3 — remove the theme divider above the card and raise the card to the
   * top of the info column so it aligns with the top of the product media. */
  function alignCardToTop(pid) {
    var wrapper = document.getElementById('mdc-wrapper-' + pid);
    var picker  = document.getElementById('mdcp-' + pid);
    if (!picker) return;

    // 1) Hide divider-like previous siblings above the card (any layout).
    var node = wrapper || picker;
    for (var depth = 0; depth < 8 && node && node.parentElement; depth++) {
      var sib = node.previousElementSibling;
      while (sib) {
        if (isDividerLike(sib)) sib.style.display = 'none';
        sib = sib.previousElementSibling;
      }
      node = node.parentElement;
    }

    // 2) If the info column is identifiable, hide its dividers and raise the card.
    var info = findInfoColumn(pid);
    if (info) {
      info.querySelectorAll('hr, [class*="divider"], [class*="separator"]').forEach(function(el) {
        if (!picker.contains(el)) el.style.display = 'none';
      });
      if (wrapper && info.firstElementChild !== wrapper) {
        info.insertBefore(wrapper, info.firstChild);
      }
    }
  }

  /* v2.13.6 — desktop: THE DETAILS under the media (left). Mobile: below the
   * card, so the title + price come first. */
  function placeDetails(pid) {
    var details = document.getElementById('mdc-details-' + pid);
    if (!details) return;

    if (_mdcMobileMQ.matches) {
      var info = findInfoColumn(pid);
      if (info) {
        if (details.parentElement !== info || info.lastElementChild !== details) info.appendChild(details);
        return;
      }
      var wrapper = document.getElementById('mdc-wrapper-' + pid);
      if (wrapper && wrapper.parentElement && wrapper.nextSibling !== details) {
        wrapper.parentElement.insertBefore(details, wrapper.nextSibling);
      }
    } else {
      var col = findMediaColumn();
      if (col && details.parentElement !== col) col.appendChild(details);
    }
  }

  function injectDetailsBlock(meta, pid) {
    if (document.getElementById('mdc-details-' + pid)) return;
    var html = buildDetailsHTML(meta, pid);
    if (!html) return;

    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var node = tmp.firstElementChild;
    if (!node) return;

    // Attach to the media column first, then position for the current viewport.
    var col = findMediaColumn();
    if (col) {
      col.appendChild(node);
    } else {
      // Fallback: keep details visible inside the picker if no media column found.
      var picker = document.getElementById('mdcp-' + pid);
      if (picker) picker.appendChild(node);
    }
    placeDetails(pid);
  }

  function refreshDetailsBlock(meta, pid) {
    if (meta && meta.details) {
      meta.details.color = 'E';
      meta.details.clarity = 'VVS2';
      if (!meta.details.caratRange) meta.details.caratRange = '1.00 - 3.50';
    }
    var live = document.getElementById('mdc-details-' + pid);
    var html = buildDetailsHTML(meta, pid);
    if (!html) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    if (!tmp.firstElementChild) return;
    if (live) {
      live.parentNode.replaceChild(tmp.firstElementChild, live);
    } else {
      injectDetailsBlock(meta, pid);
    }
    placeDetails(pid);
  }

  /* v2.13.2 — resolve a clean display name for the in-card title. */
  function resolveProductName(meta, currentParsed) {
    var name = (meta && meta.productTitle) ? String(meta.productTitle) : '';
    if (!name && currentParsed && currentParsed.style) name = currentParsed.style;
    name = String(name || '').replace(/\s*\([^)]*\)\s*$/, '').trim(); // drop trailing "(Shape)"
    name = name.replace(/^the\s+/i, '').trim();                        // v2.14.1 — drop leading "The "
    if (!name) name = (document.title || 'Product');
    return name;
  }

  /* v2.13.2 — ALWAYS render our own product title at the top of the card and
   * hide the theme's native title, so a title reliably appears above the price
   * regardless of theme markup. Uses the theme heading font (matches DETAILS).
   * v2.16 — optional brief description (custom.brief_description) under title. */
  function renderProductTitle(pid, productName, briefDescription) {
    var picker = document.getElementById('mdcp-' + pid);
    if (!picker) return;
    if (picker.querySelector('.mdc-title-slot')) return;

    // Hide the theme's native title(s) to avoid duplication.
    var sels = ['.product-title', '.product__title', '[class*="product__title"]',
                '[class*="product-title"]', '.product-information__title'];
    for (var i = 0; i < sels.length; i++) {
      document.querySelectorAll(sels[i]).forEach(function(el) {
        if (!picker.contains(el)) el.style.display = 'none';
      });
    }

    var slot = document.createElement('div');
    slot.className = 'mdc-title-slot';
    var h = document.createElement('h1');
    h.className = 'mdc-synth-title';
    h.textContent = productName || '';
    slot.appendChild(h);

    var brief = String(briefDescription || '').trim();
    if (brief) {
      var sub = document.createElement('p');
      sub.className = 'mdc-synth-brief';
      sub.textContent = brief;
      slot.appendChild(sub);
    }

    picker.insertBefore(slot, picker.firstChild);
  }

  /* v2.13 — breadcrumb: Collection / Home / Product, above the media column. */
  function injectBreadcrumb(meta, pid, productName) {
    if (document.getElementById('mdc-breadcrumb-' + pid)) return;
    var col = findMediaColumn();
    if (!col) return;

    var bc = (meta && meta.breadcrumb) || {};
    var collTitle = bc.collectionTitle || 'Diamond Engagement Rings';
    /* Homepage Shop All grid (sections/shop-all-grid.liquid) — not the old collection page. */
    var collUrl   = bc.collectionUrl   || '/#shop-all';
    var name = productName || (meta && meta.productTitle) || document.title || 'Product';

    var html = '<nav class="mdc-breadcrumb" id="mdc-breadcrumb-' + pid + '" aria-label="Breadcrumb">' +
      '<a class="mdc-breadcrumb-home" href="/">Home</a>' +
      '<span class="mdc-breadcrumb-sep" aria-hidden="true">/</span>' +
      '<a class="mdc-breadcrumb-collection" href="' + escAttr(collUrl) + '">' + escHtml(collTitle) + '</a>' +
      '<span class="mdc-breadcrumb-sep" aria-hidden="true">/</span>' +
      '<span class="mdc-breadcrumb-current" aria-current="page">' + escHtml(name) + '</span>' +
    '</nav>';

    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var node = tmp.firstElementChild;
    if (node) col.insertBefore(node, col.firstChild);
  }

  function injectRingSizeProperty(form, size) {
    if (!form) return;
    var existing = form.querySelector('input[name="properties[Ring Size]"]');
    if (size) {
      if (!existing) {
        existing = document.createElement('input');
        existing.type = 'hidden';
        existing.name = 'properties[Ring Size]';
        form.appendChild(existing);
      }
      existing.value = size;
    } else if (existing) {
      existing.remove();
    }
  }

  function injectNoDiamondProperty(form, enabled) {
    if (!form) return;
    var existing = form.querySelector('input[name="properties[Diamond]"]');
    if (enabled) {
      if (!existing) {
        existing = document.createElement('input');
        existing.type = 'hidden';
        existing.name = 'properties[Diamond]';
        form.appendChild(existing);
      }
      existing.value = NO_DIAMOND_PROP_VALUE;
    } else if (existing) {
      existing.remove();
    }
  }

  function findNativePriceEl() {
    var selectors = [
      '.price__regular .price-item--regular',
      '.product__price .price',
      '.product-single__price',
      '[data-product-price]',
      '.price .money',
      '.product__price',
      '.price'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }

  function buildPriceHTML(basePrice, diamond, noDiamond) {
    if (diamond) {
      var ringDollars    = Math.round(basePrice / 100);
      var diamondDollars = Number(diamond.price);
      var totalDollars   = ringDollars + diamondDollars;
      return '<div class="mdc-price-display" data-mdc-price>' +
        '<div class="mdc-price-line"><span class="mdc-price-total">' + fmtDollars(totalDollars) + '</span></div>' +
        '<div class="mdc-price-breakdown">' +
          '<span class="mdc-price-ring">Ring ' + fmtDollars(ringDollars) + '</span> ' +
          '<span class="mdc-price-diamond">+ Diamond ' + fmtDollars(diamondDollars) + '</span>' +
        '</div>' +
      '</div>';
    }
    if (noDiamond) {
      return '<div class="mdc-price-display" data-mdc-price>' +
        '<div class="mdc-price-line">' +
          '<span class="mdc-price-total">' + centsToDisplay(basePrice) + '</span>' +
          '<span class="mdc-price-caption">setting only \u00b7 add your own diamond</span>' +
        '</div>' +
      '</div>';
    }
    return '<div class="mdc-price-display" data-mdc-price>' +
      '<div class="mdc-price-line">' +
        '<span class="mdc-price-total">' + centsToDisplay(basePrice) + '</span>' +
        '<span class="mdc-price-caption">' + escHtml(PRICE_CAPTION) + '</span>' +
      '</div>' +
    '</div>';
  }

  function updatePriceDisplay(pid, basePrice, state) {
    var priceEl = document.querySelector('[data-mdc-price]');
    if (priceEl) {
      var tmp = document.createElement('div');
      tmp.innerHTML = buildPriceHTML(basePrice, state.diamond, state.noDiamond);
      priceEl.parentNode.replaceChild(tmp.firstElementChild, priceEl);
    }
    var native = findNativePriceEl();
    if (native && !native.closest('[data-mdc-price]')) {
      if (state.diamond) {
        native.textContent = fmtDollars(Math.round(basePrice / 100) + Number(state.diamond.price));
      } else {
        native.textContent = centsToDisplay(basePrice);
      }
    }
  }

  function findDiamondVariantId(diamondPrice, diamondVariants) {
    if (!diamondVariants) {
      console.warn('[MDC] diamondVariants is null — Lab Diamond product not found in Liquid.');
      return null;
    }
    var price = Math.round(Number(diamondPrice));
    var keys  = Object.keys(diamondVariants).map(Number).sort(function(a, b) { return a - b; });

    if (diamondVariants[String(price)]) {
      console.log('[MDC] Diamond variant exact match: $' + price + ' →', diamondVariants[String(price)]);
      return diamondVariants[String(price)];
    }
    for (var i = 0; i < keys.length; i++) {
      if (keys[i] >= price) {
        console.log('[MDC] Diamond variant rounded up: $' + price + ' → $' + keys[i]);
        return diamondVariants[String(keys[i])];
      }
    }
    if (keys.length) {
      console.warn('[MDC] Diamond price $' + price + ' exceeds all variants. Using highest.');
      return diamondVariants[String(keys[keys.length - 1])];
    }
    return null;
  }

  function refreshHorizonCart(itemCount) {
    var countSelectors = [
      '[data-cart-count]',
      '.cart-count-bubble',
      '.cart-count',
      '#cart-icon-bubble .visually-hidden',
      '[data-header-cart-quantity]',
      '.header__cart-toggle [aria-label]',
      'cart-icon-bubble',
      '.cart-link__bubble',
      'header-cart .cart-count',
      '.site-header__cart .cart-count',
      '[data-cart-item-count]',
      'a[href="/cart"] .count',
      '.cart__item-count'
    ];

    countSelectors.forEach(function(sel) {
      document.querySelectorAll(sel).forEach(function(el) {
        if (el.hasAttribute('aria-label')) {
          el.setAttribute('aria-label', itemCount + ' item' + (itemCount !== 1 ? 's' : '') + ' in your cart');
        } else {
          el.textContent = itemCount;
        }
      });
    });

    var cartLink = document.querySelector('a[href="/cart"]');
    if (cartLink) {
      cartLink.querySelectorAll('*').forEach(function(el) {
        if (el.children.length === 0 && /^\d+$/.test(el.textContent.trim())) {
          el.textContent = itemCount;
        }
      });
    }

    var events = ['cart:refresh', 'cart:updated', 'cart:change'];
    events.forEach(function(name) {
      try {
        document.dispatchEvent(new CustomEvent(name, {
          bubbles: true,
          detail: { cart: { item_count: itemCount } }
        }));
      } catch(e) {}
    });

    setTimeout(function() {
      fetch('/?sections=cart-drawer')
        .then(function(r) { return r.json(); })
        .then(function(sections) {
          var html = sections['cart-drawer'];
          if (!html) return;

          var drawerEl = document.getElementById('CartDrawer') ||
                         document.querySelector('cart-drawer');
          if (!drawerEl) return;

          if (typeof drawerEl.renderContents === 'function') {
            try { drawerEl.renderContents(sections); return; } catch(e) {}
          }

          if (typeof drawerEl.getSectionsToRender === 'function') {
            try {
              var tmp = document.createElement('div');
              tmp.innerHTML = html;
              drawerEl.getSectionsToRender().forEach(function(sctn) {
                var target = document.getElementById(sctn.id) || document.querySelector(sctn.selector);
                var fresh  = tmp.querySelector(sctn.selector || ('#' + sctn.id));
                if (target && fresh) target.innerHTML = fresh.innerHTML;
              });
              return;
            } catch(e) {}
          }

          var tmp2 = document.createElement('div');
          tmp2.innerHTML = html;

          var itemsSelectors = [
            '#CartDrawer-CartItems',
            '.cart-drawer__cart-items-wrapper',
            '.drawer__cart-items-wrapper',
            '#cart-drawer-items',
            'cart-items',
            '.cart-items'
          ];
          var totalsSelectors = [
            '#CartDrawer-Footer',
            '.totals',
            '.cart-drawer__footer',
            '.drawer__footer'
          ];

          var swapped = false;
          itemsSelectors.forEach(function(sel) {
            if (swapped) return;
            var live  = drawerEl.querySelector(sel);
            var fresh = tmp2.querySelector(sel);
            if (live && fresh) { live.innerHTML = fresh.innerHTML; swapped = true; }
          });
          totalsSelectors.forEach(function(sel) {
            var live  = drawerEl.querySelector(sel);
            var fresh = tmp2.querySelector(sel);
            if (live && fresh) live.innerHTML = fresh.innerHTML;
          });

          if (!swapped) {
            var isOpen = drawerEl.hasAttribute('open') ||
                         drawerEl.classList.contains('is-open') ||
                         drawerEl.classList.contains('active');
            if (isOpen && typeof drawerEl.open === 'function') {
              try { drawerEl.open(); } catch(e) {}
            }
          }
        })
        .catch(function(e) {
          console.warn('[MDC] Section refresh failed (non-critical):', e.message);
        });
    }, 600);
  }

  function fetchCartAdd(payload) {
    return fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify(payload)
    }).then(function(res) {
      if (!res.ok) {
        return res.json().then(function(data) {
          var msg = data.description || data.message || 'Cart error ' + res.status;
          console.error('[MDC] /cart/add.js error:', res.status, JSON.stringify(data));
          throw new Error(msg);
        });
      }
      return res.json();
    });
  }

  function attachCartIntercept(form, state, meta, errorEl) {
    if (DISABLE_DIAMOND_CART) return;
    if (!form || form.dataset.mdcIntercepted) return;
    form.dataset.mdcIntercepted = '1';

    var btn = form.querySelector('[type="submit"], [name="add"]');
    var clickTarget = btn || form;

    clickTarget.addEventListener('click', function() {
      if (!state.diamond) {
        console.log('[MDC] No diamond selected — skipping.');
        return;
      }

      var varId = findDiamondVariantId(state.diamond.price, meta.diamondVariants);
      if (!varId) {
        console.warn('[MDC] No variant found for $' + state.diamond.price);
        return;
      }

      var diamondCaratVal = state.diamond.carat != null ? state.diamond.carat : state.diamond.weight;
      var diamondProps = {
        '_mdc_diamond': '1',
        '_ring_style':  meta.currentTitle,
        'Diamond':      diamondCaratVal + 'ct ' + state.diamond.color + '-' + state.diamond.clarity +
                        (state.diamond.cutGrade ? ' ' + state.diamond.cutGrade : (state.diamond.cut ? ' ' + state.diamond.cut : '')),
        'Diamond ID':   state.diamond.id
      };
      if (state.size) {
        diamondProps['Ring Size'] = state.size;
      }

      window._mdcCartCallbacks.push({
        once: true,
        fn: function() {
          console.log('[MDC] Ring add detected — posting diamond. variantId:', varId);

          fetchCartAdd({ id: varId, quantity: 1, properties: diamondProps })
            .then(function() {
              return fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
            })
            .then(function(r) { return r.json(); })
            .then(function(cart) {
              console.log('[MDC] Diamond added. Cart items:', cart.item_count);
              refreshHorizonCart(cart.item_count);
              window.location.href = '/cart';
            })
            .catch(function(err) {
              console.error('[MDC] Diamond add failed:', err.message);
              if (errorEl) {
                errorEl.textContent = 'Diamond add failed: ' + err.message;
                errorEl.classList.add('is-visible');
              }
            });
        }
      });
    }, true);
  }

  function parseRingSizes(raw) {
    if (raw == null || raw === '') return null;
    var arr;
    if (Array.isArray(raw)) { arr = raw; }
    else if (typeof raw === 'string') {
      try { var p = JSON.parse(raw); arr = Array.isArray(p) ? p : raw.split('|'); }
      catch(e) { arr = raw.split('|'); }
    } else { return null; }
    return arr.map(function(item) {
      if (item == null) return '';
      if (typeof item === 'object') return String(item.value || item.label || item.name || item);
      return String(item).trim();
    }).filter(function(sv) { return sv !== '' && sv !== '[object Object]'; });
  }

  function parseDiamonds(raw) {
    if (!raw) return null;
    var data = typeof raw === 'string'
      ? (function() { try { return JSON.parse(raw); } catch(e) { return null; } })()
      : raw;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    return null;
  }

  // Helper: replace a rendered [data-section] block with fresh HTML.
  function replaceSection(pickerEl, sectionName, newHTML) {
    if (!newHTML) return;
    var live = pickerEl.querySelector('[data-section="' + sectionName + '"]');
    if (!live) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = newHTML;
    if (tmp.firstElementChild) {
      live.parentNode.replaceChild(tmp.firstElementChild, live);
    }
  }

  function productFromMeta(meta) {
    if (!meta || !meta.catalog || !meta.catalog.variants || !meta.catalog.variants.length) return null;
    return {
      title: meta.currentTitle,
      handle: meta.handle,
      options: meta.catalog.options || [],
      variants: meta.catalog.variants
    };
  }

  function extractProductHandle(meta) {
    var handle = (meta && meta.handle) || '';
    if (handle) return handle;
    try {
      var parts = window.location.pathname.split('/').filter(Boolean);
      var pi = parts.indexOf('products');
      if (pi !== -1 && parts[pi + 1]) return parts[pi + 1];
    } catch (e) {}
    var vp = document.querySelector('variant-picker[data-product-url]');
    if (vp && vp.dataset.productUrl) {
      var segs = vp.dataset.productUrl.split('/').filter(Boolean);
      var i = segs.indexOf('products');
      if (i !== -1 && segs[i + 1]) return segs[i + 1].split('?')[0];
    }
    if (meta && meta.currentUrl) {
      var bits = String(meta.currentUrl).split('/').filter(Boolean);
      var j = bits.indexOf('products');
      if (j !== -1 && bits[j + 1]) return bits[j + 1].split('?')[0];
    }
    return '';
  }

  function startCatalogPicker(pid, meta, product) {
    product = mergeCatalogMedia(product, meta);
    if (product && isNativeCatalog(product)) {
      initNativePicker(pid, meta, product);
    } else {
      initLegacyPicker(pid, meta);
    }
  }

  function mergeCatalogMedia(product, meta) {
    if (!product || !product.variants || !meta || !meta.catalog || !meta.catalog.variants) return product;
    var byId = {};
    meta.catalog.variants.forEach(function(v) { byId[String(v.id)] = v; });
    product.variants.forEach(function(v) {
      var extra = byId[String(v.id)];
      if (!extra) return;
      if (extra.featured_media_id) v.featured_media_id = extra.featured_media_id;
      if (!v.featured_image && extra.featured_image) v.featured_image = extra.featured_image;
      if (v.featured_image && extra.featured_media_id && !v.featured_image.id) {
        v.featured_image.id = extra.featured_media_id;
      }
    });
    return product;
  }

  function initPicker() {
    var pid  = null;
    var vpEl = document.querySelector('variant-picker[data-product-id]');
    if (vpEl) pid = vpEl.dataset.productId;
    if (!pid) {
      try {
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product)
          pid = String(window.ShopifyAnalytics.meta.product.id);
      } catch(e) {}
    }
    if (!pid) {
      var metaProduct = document.querySelector('meta[property="product:id"]');
      if (metaProduct) pid = metaProduct.getAttribute('content');
    }
    if (!pid) {
      var jcKeys = Object.keys(window._jcMeta || {});
      if (jcKeys.length === 1) pid = jcKeys[0];
    }
    if (!pid) { console.warn('[MDC] could not resolve product ID'); return; }
    if (window._mdcInjected[pid]) return;

    var meta = window._jcMeta && window._jcMeta[pid];
    if (!meta) { console.warn('[MDC] no meta for pid:', pid); return; }

    window._mdcInjected[pid] = true;

    if (meta && meta.priceCaption) PRICE_CAPTION = String(meta.priceCaption);

    if (NATIVE_VARIANT_MODE) {
      var fromMeta = productFromMeta(meta);
      var handle = extractProductHandle(meta);
      if (handle) {
        fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
          .then(function(r) { return r.ok ? r.json() : null; })
          .then(function(product) {
            startCatalogPicker(pid, meta, product || fromMeta);
          })
          .catch(function() { startCatalogPicker(pid, meta, fromMeta); });
        return;
      }
      startCatalogPicker(pid, meta, fromMeta);
      return;
    }

    initLegacyPicker(pid, meta);
  }

  function initNativePicker(pid, meta, product) {
    var variants = product.variants || [];
    window._mdcNativeProduct = product;
    window._mdcOptionMap = catalogOptionMap(product);
    hideNativeVariantChrome();
    if (!window._mdcVariantUpdateBound) {
      window._mdcVariantUpdateBound = true;
      document.addEventListener('variant:update', function() {
        hideNativeVariantChrome();
        if (window._mdcGalleryVariant) {
          setTimeout(function() { applyVariantMedia(window._mdcGalleryVariant); }, 60);
        }
      });
    }
    var urlVid = getVariantIdFromUrl();
    var current = null;
    if (urlVid) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(urlVid)) { current = variants[i]; break; }
      }
    }
    if (!current) current = resolveDefaultDisplayVariant(variants);

    var metals = uniqMappedOption(variants, 'metal');
    var shapes = uniqMappedOption(variants, 'shape');
    var carats = uniqMappedOption(variants, 'carat');
    carats.sort(function(a, b) {
      return parseFloat(a) - parseFloat(b);
    });

    var ringSizes       = parseRingSizes(meta.ringSize) || ['4','4.5','5','5.5','6','6.5','7','7.5','8','8.5','9','9.5','10'];
    var defaultRingSize = resolveDefaultRingSize(ringSizes);
    var state = { diamond: null, noDiamond: false, size: defaultRingSize, variant: current };
    var basePrice = Number(current.price) || Number(meta.basePrice) || 0;

    if (meta.details) {
      meta.details.color = 'E';
      meta.details.clarity = 'VVS2';
      meta.details.caratRange = '1.00 - 3.50';
      meta.details.metal = variantMetal(current) || meta.details.metal;
      meta.details.compatibleShapes = shapes;
    }
    meta.sku = current.sku || meta.sku;
    meta.shapeSiblings = meta.shapeSiblings || {};

    var vpEl = document.querySelector('variant-picker[data-product-id]');
    if (vpEl) {
      vpEl.querySelectorAll('fieldset').forEach(function(fs) { fs.style.display = 'none'; });
    }

    var pickerHTML =
      '<div class="mdc-picker" id="mdcp-' + pid + '">' +
        buildPriceHTML(basePrice, null) +
        buildNativeSelect('Shape', 'shape', shapes, variantShape(current), function(v) {
          return shapeDisplayName(normalizeShapeKey(v) || v);
        }) +
        buildNativeSelect('Metal', 'metal', metals, variantMetal(current)) +
        buildNativeSelect('Center Carat Size', 'carat', carats, variantCarat(current), caratDisplayLabel) +
        buildRingSizeHTML(ringSizes, state) +
        '<div class="mdc-cart-error" data-mdc-cart-error></div>' +
      '</div>';

    var wrapper = document.createElement('div');
    wrapper.id = 'mdc-wrapper-' + pid;
    wrapper.innerHTML = pickerHTML;

    var buyButtons = document.querySelector('[ref="productFormButtons"]') ||
                     document.querySelector('.product-form__buttons') ||
                     document.querySelector('.buy-buttons');
    var cartForm = document.querySelector('form[action*="/cart/add"]');

    if (buyButtons) {
      buyButtons.parentNode.insertBefore(wrapper, buyButtons);
    } else if (vpEl) {
      vpEl.parentNode.insertBefore(wrapper, vpEl.nextSibling);
    } else if (cartForm) {
      cartForm.parentNode.insertBefore(wrapper, cartForm);
    } else {
      console.warn('[MDC] no insertion point found');
      return;
    }

    var nativePrice = findNativePriceEl();
    if (nativePrice) {
      var priceContainer = nativePrice.closest('.price, .product__price, .product-single__price, [data-product-price]');
      if (priceContainer) priceContainer.style.display = 'none';
      else nativePrice.style.display = 'none';
    }

    var pickerEl = document.getElementById('mdcp-' + pid);
    if (!pickerEl) return;

    var sizeSelect = pickerEl.querySelector('[data-mdc-ring-size]');
    if (sizeSelect) {
      syncRingSizeDefaultDisplay(sizeSelect);
      sizeSelect.addEventListener('change', function() {
        state.size = sizeSelect.value;
        injectRingSizeProperty(cartForm, state.size);
        syncRingSizeDefaultDisplay(sizeSelect);
      });
    }
    if (state.size) injectRingSizeProperty(cartForm, state.size);

    pickerEl.addEventListener('change', function(e) {
      var sel = e.target.closest('select[data-mdc-opt]');
      if (!sel) return;
      var metal = (pickerEl.querySelector('select[data-mdc-opt="metal"]') || {}).value;
      var shape = (pickerEl.querySelector('select[data-mdc-opt="shape"]') || {}).value;
      var carat = (pickerEl.querySelector('select[data-mdc-opt="carat"]') || {}).value;
      var next = findNativeVariant(variants, metal, shape, carat);
      if (!next) return;
      setSelectValue(pickerEl.querySelector('select[data-mdc-opt="metal"]'), variantMetal(next));
      setSelectValue(pickerEl.querySelector('select[data-mdc-opt="shape"]'), variantShape(next));
      setSelectValue(pickerEl.querySelector('select[data-mdc-opt="carat"]'), variantCarat(next));
      applyNativeVariant(pid, meta, product, next, state);
    });

    if (SHOW_PRODUCT_TITLE) {
      renderProductTitle(pid, resolveProductName(meta, { style: product.title }), meta && meta.briefDescription);
    }
    alignCardToTop(pid);
    injectBreadcrumb(meta, pid, resolveProductName(meta, { style: product.title }));
    injectDetailsBlock(meta, pid);
    applyNativeVariant(pid, meta, product, current, state);

    (function () {
      var handler = function () { placeDetails(pid); };
      try { _mdcMobileMQ.addEventListener('change', handler); }
      catch (e) { if (_mdcMobileMQ.addListener) _mdcMobileMQ.addListener(handler); }
    })();

    console.log('[MDC] v2.15.3 native variants | pid:', pid, '| variants:', variants.length,
      '| metal:', variantMetal(current), '| shape:', variantShape(current), '| carat:', variantCarat(current),
      '| mediaId:', featuredMediaId(current), '| mediaSrc:', featuredImageSrc(current) ? 'yes' : 'no');
  }

  function initLegacyPicker(pid, meta) {
    var currentParsed = inferCurrentParsed(meta);
    var vpEl = document.querySelector('variant-picker[data-product-id]');
    if (!parseTitle(meta.currentTitle)) {
      console.warn('[MDC] currentTitle not in strict format; using fallback inference:', meta.currentTitle, currentParsed);
    }

    var productShapeKey = resolveProductShapeKey(meta);
    var shapeFilter     = productShapeKey || inferShapeFromUrl(window.location.pathname);
    var idx             = buildSiblingsIndex(currentParsed, meta.siblings || {}, shapeFilter);
    var ringSizes        = parseRingSizes(meta.ringSize) || ['4','4.5','5','5.5','6','6.5','7','7.5','8','8.5','9','9.5','10'];
    var defaultRingSize  = resolveDefaultRingSize(ringSizes); // v2.14.3
    var diamonds        = parseDiamonds(meta.availableDiamonds) || [];
    if (diamonds.length) {
      diamonds.reverse();
    }
    var basePrice       = Number(meta.basePrice || meta.ringPriceCents) || 0;
    var diamondVariants = meta.diamondVariants || null;
    var productCertUrl  = getHardcodedCertUrlByShape(productShapeKey);

    // Ensure a shapeSiblings container exists (Liquid v2.3 emits it; guard for older bridges).
    meta.shapeSiblings = meta.shapeSiblings || {};
    // Guarantee the current shape is always selectable.
    if (productShapeKey) {
      var curShapeName = productShapeKey.charAt(0).toUpperCase() + productShapeKey.slice(1);
      if (!meta.shapeSiblings[curShapeName]) {
        meta.shapeSiblings[curShapeName] = meta.currentUrl || (window.location.pathname + window.location.search);
      }
    }

    // v2.14.3 — state.size initializes to the resolved default (e.g. "6.5")
    // instead of '', so the picker, cart property, and console all agree
    // before the shopper touches anything.
    var state = { diamond: null, noDiamond: false, size: defaultRingSize, productShapeKey: productShapeKey, productCertUrl: productCertUrl };

    if (vpEl) {
      vpEl.querySelectorAll('fieldset').forEach(function(fs) { fs.style.display = 'none'; });
    }

    console.log('[MDC] v2.14.4 | pid:', pid,
      '| productShapeKey:', productShapeKey,
      '| shapeFilter:', shapeFilter,
      '| productCertUrl:', productCertUrl,
      '| basePrice:', basePrice,
      '| defaultRingSize:', defaultRingSize || '(none — product has no 6.5)',
      '| diamonds:', flattenDiamonds(diamonds).length,
      '| diamondVariants:', diamondVariants
        ? Object.keys(diamondVariants).length + ' tiers'
        : 'NOT SET',
      '| liquidSiblings:', Object.keys(meta.siblings || {}).length,
      '| shapeSiblings:', Object.keys(meta.shapeSiblings).length);

    var cards   = buildDiamondCardsHTML(state, diamonds);
    var hasMore = cards.count > FIRST_ROW_COUNT;

    var pickerHTML =
      '<div class="mdc-picker" id="mdcp-' + pid + '">' +
        buildPriceHTML(basePrice, null) +
        buildShapeDropdownHTML(meta, productShapeKey) +
        buildMetalDropdownHTML(currentParsed, idx) +
        buildCaratDropdownHTML(currentParsed, idx) +
        buildRingSizeHTML(ringSizes, state) +
        (SHOW_DIAMOND_PICKER
          ? '<div class="mdc-section mdc-diamond-section' + (hasMore ? '' : ' is-expanded') + '" data-section="diamond">' +
              '<div class="mdc-diamond-header">' +
                '<span class="mdc-diamond-title">Choose Your Lab Diamond</span>' +
                (hasMore
                  ? '<button type="button" class="mdc-diamond-see-more" data-see-more>See more</button>'
                  : '') +
              '</div>' +
              '<div class="mdc-diamond-grid" id="mdc-diamond-grid-' + pid + '">' +
                cards.html +
              '</div>' +
            '</div>'
          : '') +
        '<div class="mdc-cart-error" data-mdc-cart-error></div>' +
      '</div>';

    var wrapper = document.createElement('div');
    wrapper.id = 'mdc-wrapper-' + pid;
    wrapper.innerHTML = pickerHTML;

    var buyButtons = document.querySelector('[ref="productFormButtons"]') ||
                     document.querySelector('.product-form__buttons') ||
                     document.querySelector('.buy-buttons');
    var cartForm = document.querySelector('form[action*="/cart/add"]');

    if (buyButtons) {
      buyButtons.parentNode.insertBefore(wrapper, buyButtons);
    } else if (vpEl) {
      vpEl.parentNode.insertBefore(wrapper, vpEl.nextSibling);
    } else if (cartForm) {
      cartForm.parentNode.insertBefore(wrapper, cartForm);
    } else {
      console.warn('[MDC] no insertion point found');
      return;
    }

    var nativePrice = findNativePriceEl();
    if (nativePrice) {
      var priceContainer = nativePrice.closest('.price, .product__price, .product-single__price, [data-product-price]');
      if (priceContainer) priceContainer.style.display = 'none';
      else nativePrice.style.display = 'none';
    }

    var errorEl = wrapper.querySelector('[data-mdc-cart-error]');
    var pickerEl = document.getElementById('mdcp-' + pid);
    if (!pickerEl) return;

    // Ring size — property injection only (no navigation).
    var sizeSelect = pickerEl.querySelector('[data-mdc-ring-size]');
    if (sizeSelect) {
      syncRingSizeDefaultDisplay(sizeSelect);
      sizeSelect.addEventListener('change', function() {
        state.size = sizeSelect.value;
        injectRingSizeProperty(cartForm, state.size);
        syncRingSizeDefaultDisplay(sizeSelect);
      });
    }

    // v2.14.3 — seed the hidden properties[Ring Size] input with the default
    // right away, so a shopper who never opens the dropdown still adds a ring
    // with the "6.5 (Default)" size attached, matching what's displayed.
    if (state.size) injectRingSizeProperty(cartForm, state.size);

    if (!DISABLE_DIAMOND_CART) attachCartIntercept(cartForm, state, meta, errorEl);

    // Unified sibling navigation for shape / metal / carat dropdowns.
    pickerEl.addEventListener('change', function(e) {
      var sel = e.target.closest('select[data-mdc-nav]');
      if (!sel) return;
      if (DISABLE_SIBLING_NAV) return;
      var url = sel.value;
      if (url) { window.location.href = url; }
    });

    // Diamond selection (stone card OR Setting Only) + preview trigger.
    pickerEl.addEventListener('click', function(e) {
      var previewTrigger = e.target.closest('[data-preview-trigger]');
      var previewCard = previewTrigger && e.target.closest('.mdc-diamond-card');
      if (previewTrigger && previewCard) {
        e.preventDefault();
        e.stopPropagation();
        try {
          var previewStone = JSON.parse(previewCard.dataset.diamondJson);
          openPreviewModal(previewStone, state.productShapeKey, state.productCertUrl);
        } catch (err) {}
        return;
      }

      var card = e.target.closest('.mdc-diamond-card');
      if (!card) return;

      if (card.hasAttribute('data-diamond-none')) {
        state.diamond   = null;
        state.noDiamond = true;
        injectNoDiamondProperty(cartForm, true);
      } else {
        try { state.diamond = JSON.parse(card.dataset.diamondJson); } catch(err) { return; }
        state.noDiamond = false;
        injectNoDiamondProperty(cartForm, false);
      }

      pickerEl.querySelectorAll('.mdc-diamond-card').forEach(function(c) {
        c.classList.toggle('is-selected', c === card);
      });
      updatePriceDisplay(pid, basePrice, state);
    });

    // See more / See less.
    pickerEl.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-see-more]');
      if (!btn) return;
      var section = pickerEl.querySelector('.mdc-diamond-section');
      if (!section) return;
      var expanded = section.classList.toggle('is-expanded');
      btn.textContent = expanded ? 'See less' : 'See more';
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closePreviewModal();
    });

    console.log('[MDC] ready | pid:', pid,
      '| style:', currentParsed.style,
      '| metal:', currentParsed.metal,
      '| carat:', currentParsed.carat,
      '| productShapeKey:', state.productShapeKey,
      '| ringSize:', state.size || '(none)',
      '| fetch patch:', !!window._mdcFetchPatched,
      '| metals:', idx.metals.length,
      '| carats:', idx.carats.length,
      '| shapes:', Object.keys(meta.shapeSiblings).length
    );

    // v2.13.2 — product title inside the card, above the price.
    // v2.16 — brief description under title when metafield is set.
    if (SHOW_PRODUCT_TITLE) {
      renderProductTitle(pid, resolveProductName(meta, currentParsed), meta && meta.briefDescription);
    }
    // v2.13.3 — remove theme dividers above the card and raise it to the top.
    alignCardToTop(pid);

    // v2.13 — breadcrumb above the media; THE DETAILS below it.
    injectBreadcrumb(meta, pid, resolveProductName(meta, currentParsed));
    injectDetailsBlock(meta, pid);

    // v2.13.6 — re-place THE DETAILS when crossing the mobile breakpoint.
    (function () {
      var handler = function () { placeDetails(pid); };
      try { _mdcMobileMQ.addEventListener('change', handler); }
      catch (e) { if (_mdcMobileMQ.addListener) _mdcMobileMQ.addListener(handler); }
    })();

    /* Async enrichment: metal/carat siblings + shape siblings when thin. */
    var shapeCount = Object.keys(meta.shapeSiblings).length;
    if (!DISABLE_STORE_PRODUCT_FETCH && (idx.metals.length < 3 || idx.carats.length < 2 || shapeCount < 2)) {
      var _styleKey    = normalizeStyleKey(currentParsed.style);
      var _shapeFilter = shapeFilter;
      var _parsed      = currentParsed;
      var _pickerEl    = pickerEl;
      var _prevMetals  = idx.metals.length;
      var _prevCarats  = idx.carats.length;
      var _prevShapes  = shapeCount;

      console.log('[MDC] Data thin (' + _prevMetals + ' metals, ' + _prevCarats + ' carats, ' + _prevShapes + ' shapes). Fetching from API...');

      fetchAllStoreProducts(function(products) {
        /* ---- metal/carat enrichment (same-shape siblings) ---- */
        var apiSiblings = findSiblingsFromProducts(products, _styleKey, _shapeFilter);

        var merged = {};
        var existingKeys = Object.keys(meta.siblings || {});
        var k;
        for (k = 0; k < existingKeys.length; k++) {
          merged[existingKeys[k]] = meta.siblings[existingKeys[k]];
        }
        var apiKeys = Object.keys(apiSiblings);
        for (k = 0; k < apiKeys.length; k++) {
          merged[apiKeys[k]] = apiSiblings[apiKeys[k]];
        }
        merged[meta.currentTitle] = meta.currentUrl;

        var newIdx = buildSiblingsIndex(_parsed, merged, _shapeFilter);
        console.log('[MDC] API metal/carat: ' + newIdx.metals.length + ' metals, ' + newIdx.carats.length + ' carats (was ' + _prevMetals + '/' + _prevCarats + ')');

        if (newIdx.metals.length > _prevMetals || newIdx.carats.length > _prevCarats) {
          replaceSection(_pickerEl, 'metal', buildMetalDropdownHTML(_parsed, newIdx));
          replaceSection(_pickerEl, 'carat', buildCaratDropdownHTML(_parsed, newIdx));
          meta.siblings = merged;
        }

        /* ---- shape enrichment (cross-shape siblings) ---- */
        var apiShapes = findShapeSiblingsFromProducts(products, _styleKey);
        var shapeKeys = Object.keys(apiShapes);
        var addedShape = false;
        for (k = 0; k < shapeKeys.length; k++) {
          if (!meta.shapeSiblings[shapeKeys[k]]) {
            meta.shapeSiblings[shapeKeys[k]] = apiShapes[shapeKeys[k]];
            addedShape = true;
          }
        }
        if (addedShape) {
          console.log('[MDC] API shapes: ' + Object.keys(meta.shapeSiblings).length + ' (was ' + _prevShapes + ')');
          replaceSection(_pickerEl, 'shape', buildShapeDropdownHTML(meta, productShapeKey));
          // Details block lists shapes too — refresh it in the media column.
          refreshDetailsBlock(meta, pid);
        }

        console.log('[MDC] Async enrichment complete.');
      });
    }
  }

  function boot() {
    injectFonts();
    injectCSS();
    initPicker();
    setTimeout(function() { if (!Object.keys(window._mdcInjected).length) initPicker(); }, 400);
    setTimeout(function() { if (!Object.keys(window._mdcInjected).length) initPicker(); }, 1200);
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); }
  else { boot(); }

})();